// ============================================================
// avatarPage.js — Página "Generación de avatar"
// ------------------------------------------------------------
// - Pestañas de modo (Guion a vídeo / Foto a vídeo)
// - Selector de modelo de avatar (dropdown)
// - Selector de calidad (dropdown)
// - Selector de voz (panel lateral deslizante)
// - Selección de avatar en el grid
// - Botón Generar
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { showAdminCreditModal, showConfirmModal } from './subscriptionModal.js';
import { getUploadsUrl, sanitizeMediaUrl, uploadAttachment, apiRequest, generateAvatar, listHistory, getJobStatus } from './api.js';

export function initAvatarPage() {
  if (document.body.dataset.page !== 'avatar') return;

  let clonameUploadedFile = null;

  function saveCustomAvatarToStorage(url, name, isVideo) {
    if (!url || typeof url !== 'string' || url.startsWith('data:') || url.startsWith('blob:')) return;
    try {
      const saved = JSON.parse(localStorage.getItem('vimo_custom_avatars') || '[]');
      const filtered = saved.filter(a => a && a.url && a.url !== url && !a.url.startsWith('data:') && !a.url.startsWith('blob:'));
      filtered.unshift({ url, name, isVideo });
      localStorage.setItem('vimo_custom_avatars', JSON.stringify(filtered.slice(0, 15)));
    } catch (e) {
      console.error('Error al guardar avatar en storage:', e);
    }
  }

  function addNewAvatarToGrid(url, name, isVideo, select = true) {
    const grid = qs('.avatar-grid');
    if (!grid) return;

    // Evitar añadir tarjetas duplicadas para el mismo avatar URL
    const existingCard = qsa('.av-thumb-card', grid).find(c => c.dataset.avatar === url);
    if (existingCard) {
      if (select) {
        qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
        existingCard.classList.add('is-selected');
      }
      return;
    }

    if (select) {
      // Deseleccionar el resto si se solicita selección
      qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
    }

    // Guardar en localStorage para persistencia (solo si no es base64 data URL o blob)
    saveCustomAvatarToStorage(url, name, isVideo);

    const newCard = document.createElement('div');
    newCard.className = `av-thumb-card ${select ? 'is-selected' : ''}`;
    newCard.dataset.type = 'subida';
    newCard.setAttribute('role', 'button');
    newCard.setAttribute('tabindex', '0');
    newCard.dataset.avatar = url;

    const shortName = name.length > 15 ? name.slice(0, 15) + '…' : name;

    if (isVideo) {
      newCard.innerHTML = `
        <video class="av-thumb-img" src="${url}" poster="${url}" autoplay loop muted playsinline></video>
        <span class="av-thumb-name">${shortName}</span>
        <div class="av-thumb-actions">
          <button class="av-gen-act-btn btn-del av-thumb-del-btn" type="button" title="Eliminar foto">
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      `;
    } else {
      newCard.innerHTML = `
        <img class="av-thumb-img" src="${url}" alt="${name}" loading="lazy">
        <span class="av-thumb-name">${shortName}</span>
        <div class="av-thumb-actions">
          <button class="av-gen-act-btn btn-del av-thumb-del-btn" type="button" title="Eliminar foto">
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      `;
    }

    // Insertar siempre DESPUÉS del botón de subir nueva foto (#btnUploadAvatarCard)
    const uploadBtn = qs('#btnUploadAvatarCard', grid);
    if (uploadBtn) {
      uploadBtn.after(newCard);
    } else {
      grid.appendChild(newCard);
    }
  }

  // Cargar avatares personalizados guardados en visitas anteriores (limpiando duplicados y URLs efímeras)
  try {
    const rawSaved = JSON.parse(localStorage.getItem('vimo_custom_avatars') || '[]');
    const cleanSaved = [];
    const seenUrls = new Set();
    rawSaved.forEach(item => {
      if (item && item.url && !item.url.startsWith('data:') && !item.url.startsWith('blob:') && !seenUrls.has(item.url)) {
        seenUrls.add(item.url);
        cleanSaved.push(item);
      }
    });
    localStorage.setItem('vimo_custom_avatars', JSON.stringify(cleanSaved.slice(0, 15)));

    cleanSaved.reverse().forEach(item => {
      addNewAvatarToGrid(item.url, item.name, item.isVideo, false);
    });
  } catch (e) {}

  // Corregir URLs de avatares predefinidos dinámicamente para soportar producción
  const uploadsUrl = getUploadsUrl();
  qsa('.av-thumb-img').forEach(el => {
    const src = el.getAttribute('src');
    if (src && src.includes('localhost:4001/uploads')) {
      const filename = src.substring(src.lastIndexOf('/') + 1);
      el.src = `${uploadsUrl}/${filename}`;
    }
    const poster = el.getAttribute('poster');
    if (poster && poster.includes('localhost:4001/uploads')) {
      const posterName = poster.substring(poster.lastIndexOf('/') + 1);
      el.setAttribute('poster', `${uploadsUrl}/${posterName}`);
    }
  });

  // ── Referencias ──────────────────────────────────────────
  const voiceModal      = qs('#voiceModal');
  const createVoiceModal = qs('#createVoiceModal');
  const audioModal      = qs('#audioModal');
  const audioModalTitle = qs('#audioModalTitle');
  const cloneModal      = qs('#cloneModal');
  const clonamodalRoot  = qs('#clonamodalRoot');
  const scriptArea      = qs('#avatarScript');
  const scriptCount     = qs('#scriptCount');
  const selectVoiceBtn  = qs('[data-action="select-voice"]');

  // ── Pestañas de modo ─────────────────────────────────────
  const avModelDropup = qs('#avModelDropup');

  on(document, 'click', '[data-action="av-tab"]', (e, btn) => {
    const tab = btn.dataset.tab;   // 'guion' | 'foto' | 'mis-creaciones'
    qsa('.avatar-tab').forEach(t => t.classList.remove('is-active'));
    btn.classList.add('is-active');

    // Banners — solo aplican a guion y foto
    const infoGuion = qs('#avTabInfoGuion');
    const infoFoto  = qs('#avTabInfoFoto');
    if (infoGuion && infoFoto) {
      infoGuion.style.display = tab === 'guion' ? 'flex' : 'none';
      infoFoto.style.display  = tab === 'foto'  ? 'flex' : 'none';
    }

    if (tab === 'mis-creaciones') {
      // Panel exclusivo de creaciones — ocultar el de avatares
      qsa('.av-stage-view').forEach(v => {
        v.classList.toggle('is-hidden', v.dataset.stage !== 'mis-creaciones');
      });
      const scriptArea = qs('.avatar-script-area');
      const toolbar    = qs('.avatar-toolbar');
      if (scriptArea) scriptArea.style.display = 'none';
      if (toolbar)    toolbar.style.display    = 'none';
      if (avModelDropup) avModelDropup.classList.add('is-hidden');
      loadRecentAvatarGenerations();
    } else {
      // Modo guion o foto: mostrar panel de avatares compartido
      const scriptArea = qs('.avatar-script-area');
      const toolbar    = qs('.avatar-toolbar');
      if (scriptArea) scriptArea.style.display = '';
      if (toolbar)    toolbar.style.display    = '';

      qsa('.av-stage-view').forEach(v => {
        v.classList.toggle('is-hidden', v.dataset.stage !== 'avatares');
      });

      // Filtrar pills: av-pill-guion solo en guion, av-pill-foto solo en foto
      // (las pills con ambas clases se muestran siempre)
      qsa('.av-subnav-btn').forEach(pill => {
        const forGuion = pill.classList.contains('av-pill-guion');
        const forFoto  = pill.classList.contains('av-pill-foto');
        if (tab === 'guion') {
          pill.style.display = forGuion ? '' : 'none';
        } else {
          // modo foto: mostrar solo las que tienen av-pill-foto
          pill.style.display = forFoto ? '' : 'none';
        }
      });

      // En modo foto: seleccionar pill "Fotos Subidas" automáticamente
      if (tab === 'foto') {
        const misFotosBtn = qs('.av-subnav-btn[data-subtab="mis-fotos"]');
        if (misFotosBtn) {
          qsa('.av-subnav-btn').forEach(b => {
            b.classList.remove('is-active');
            b.style.background  = 'rgba(255,255,255,0.05)';
            b.style.borderColor = 'rgba(255,255,255,0.1)';
            b.style.color       = '#94a3b8';
          });
          misFotosBtn.classList.add('is-active');
          misFotosBtn.style.background  = 'rgba(168,85,247,0.18)';
          misFotosBtn.style.borderColor = 'rgba(168,85,247,0.5)';
          misFotosBtn.style.color       = '#fff';
          filterAvatarCards('mis-fotos');
        }
      }

      // Selector de modelo solo en guion
      if (avModelDropup) avModelDropup.classList.toggle('is-hidden', tab === 'foto');
    }
  });

  /**
   * Carga TODAS las creaciones de avatar (guion + foto a vídeo) desde la API
   * y las renderiza en el panel #avAllCreacionesList (pestaña "Mis Creaciones").
   */
  const HEYGEN_AVATAR_PREVIEWS = {
    annie: '/uploads/avatar_annie.png',
    shawn: '/uploads/avatar_shawn.png',
    sofia: '/uploads/avatar_sofia.png',
    david: '/uploads/avatar_david.png',
    clara: '/uploads/avatar_clara.png',
    marcos: '/uploads/avatar_marcos.png',
    nadia: '/uploads/avatar_nadia.png',
    denise: '/uploads/avatar_denise.png',
    leo: '/uploads/avatar_leo.png'
  };

  function getAvatarPreviewUrl(slug) {
    const key = (slug || '').toLowerCase().trim();
    return sanitizeMediaUrl(HEYGEN_AVATAR_PREVIEWS[key] || `/uploads/avatar_${key}.png` || HEYGEN_AVATAR_PREVIEWS.annie);
  }

  function openAvatarPreviewModal(url, title = 'Vídeo de Avatar') {
    let modal = qs('#avVideoPreviewModal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'avVideoPreviewModal';
      modal.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.85);backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;padding:20px;opacity:0;transition:opacity 0.2s ease;';
      modal.innerHTML = `
        <div style="background:#18181b;border:1px solid rgba(255,255,255,0.15);border-radius:16px;max-width:720px;width:100%;overflow:hidden;box-shadow:0 25px 50px -12px rgba(0,0,0,0.7);transform:scale(0.95);transition:transform 0.2s ease;">
          <div style="padding:14px 18px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid rgba(255,255,255,0.1);">
            <h4 id="avModalTitle" style="font-size:0.95rem;font-weight:600;margin:0;color:white;display:flex;align-items:center;gap:8px;">
              <i class="bi bi-play-circle-fill" style="color:#c084fc;"></i> <span>${escapeHtml(title)}</span>
            </h4>
            <button type="button" id="avModalCloseBtn" style="background:none;border:none;color:#a1a1aa;font-size:1.2rem;cursor:pointer;"><i class="bi bi-x-lg"></i></button>
          </div>
          <div style="position:relative;background:#000;display:flex;align-items:center;justify-content:center;max-height:65vh;">
            <video id="avModalVideo" controls autoplay playsinline style="width:100%;max-height:65vh;object-fit:contain;"></video>
          </div>
          <div style="padding:12px 18px;display:flex;align-items:center;justify-content:space-between;background:rgba(255,255,255,0.02);">
            <span style="font-size:0.75rem;color:#71717a;"><i class="bi bi-magic"></i> Reproductor Vimo AI Avatar</span>
            <a id="avModalDownloadBtn" href="${url}" download target="_blank" class="btn btn-sm" style="background:linear-gradient(135deg,#c084fc,#a855f7);color:white;font-weight:600;font-size:0.8rem;border-radius:8px;padding:6px 14px;text-decoration:none;">
              <i class="bi bi-download"></i> Descargar MP4
            </a>
          </div>
        </div>
      `;
      document.body.appendChild(modal);

      const closeBtn = modal.querySelector('#avModalCloseBtn');
      if (closeBtn) closeBtn.addEventListener('click', closeAvatarPreviewModal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeAvatarPreviewModal();
      });
    }

    const titleEl = modal.querySelector('#avModalTitle span');
    const videoEl = modal.querySelector('#avModalVideo');
    const dlBtn = modal.querySelector('#avModalDownloadBtn');

    if (titleEl) titleEl.textContent = title;
    if (videoEl) {
      videoEl.src = url;
      videoEl.play().catch(err => console.warn('Autoplay prevented:', err));
    }
    if (dlBtn) dlBtn.href = url;

    modal.style.display = 'flex';
    setTimeout(() => {
      modal.style.opacity = '1';
      if (modal.firstElementChild) modal.firstElementChild.style.transform = 'scale(1)';
    }, 10);
  }

  function closeAvatarPreviewModal() {
    const modal = qs('#avVideoPreviewModal');
    if (!modal) return;
    modal.style.opacity = '0';
    if (modal.firstElementChild) modal.firstElementChild.style.transform = 'scale(0.95)';
    const videoEl = modal.querySelector('#avModalVideo');
    if (videoEl) {
      videoEl.pause();
      videoEl.src = '';
    }
    setTimeout(() => {
      modal.style.display = 'none';
    }, 200);
  }

  async function loadAllCreaciones() {
    const container = qs('#avAllCreacionesList');
    if (!container) return;

    container.innerHTML = `
      <div style="padding:24px;text-align:center;color:#a1a1aa;font-size:0.83rem;">
        <span class="spinner-border spinner-border-sm" style="color:#c084fc;"></span>
        <span style="margin-left:8px;">Cargando creaciones…</span>
      </div>`;

    try {
      const allHistory = await listHistory();
      const avatarItems = (allHistory || []).filter(item =>
        item.type === 'avatar' || item.type === 'video' || (item.model && item.model.toLowerCase().includes('avatar'))
      );

      if (avatarItems.length === 0) {
        container.innerHTML = `
          <div style="padding:32px;text-align:center;background:rgba(255,255,255,0.02);border:1px dashed rgba(255,255,255,0.1);border-radius:12px;color:#a1a1aa;font-size:0.85rem;">
            <i class="bi bi-collection-play" style="font-size:2.5rem;display:block;margin-bottom:10px;opacity:0.25;"></i>
            Aún no tienes creaciones de avatar.<br>
            <span style="font-size:0.78rem;color:#52525b;">Genera tu primer vídeo desde <strong>Guion a vídeo</strong> o <strong>Foto a vídeo</strong>.</span>
          </div>`;
        return;
      }

      container.innerHTML = avatarItems.map(item => {
        let metaObj = {};
        try { if (typeof item.meta === 'string') metaObj = JSON.parse(item.meta); else if (item.meta) metaObj = item.meta; } catch {}

        const rawItemUrl = item.resultUrl || item.url || '';
        const itemUrl = sanitizeMediaUrl(rawItemUrl);
        const avatarId = (metaObj.avatarId || 'annie').toLowerCase();
        const metaPhoto = sanitizeMediaUrl(metaObj.photoUrl || metaObj.photo || '');
        const isUserPhoto = metaPhoto && metaPhoto.includes('/uploads/') && !metaPhoto.includes('avatar_');
        const poster = isUserPhoto ? metaPhoto : getAvatarPreviewUrl(avatarId);

        const isPending = item.status === 'queued' || item.status === 'processing';
        const isFailed  = item.status === 'failed';
        const isReady   = !isPending && !isFailed && !!itemUrl;

        const badge = isPending
          ? `<span style="background:rgba(99,102,241,0.85);color:white;padding:3px 8px;border-radius:10px;font-size:0.71rem;display:inline-flex;align-items:center;gap:4px;"><span class="spinner-border spinner-border-sm"></span>Generando</span>`
          : isFailed
            ? `<span style="background:rgba(239,68,68,0.85);color:white;padding:3px 8px;border-radius:10px;font-size:0.71rem;"><i class="bi bi-x-circle"></i> Error</span>`
            : `<span style="background:rgba(16,185,129,0.85);color:white;padding:3px 8px;border-radius:10px;font-size:0.71rem;"><i class="bi bi-check2-circle"></i> Listo</span>`;

        const modeTag = metaObj.mode === 'foto'
          ? `<span style="background:rgba(168,85,247,0.2);color:#c084fc;padding:2px 6px;border-radius:8px;font-size:0.69rem;flex-shrink:0;"><i class="bi bi-image"></i> Foto</span>`
          : `<span style="background:rgba(99,102,241,0.2);color:#818cf8;padding:2px 6px;border-radius:8px;font-size:0.69rem;flex-shrink:0;"><i class="bi bi-file-text"></i> Guion</span>`;

        return `
          <div class="av-gen-card" data-id="${item.id}" data-job-id="${item.jobId||''}" data-url="${itemUrl}" data-title="${escapeHtml(item.title || item.prompt || 'Avatar')}" style="background:var(--color-bg-surface-2,#1c1c1e);border-radius:12px;padding:12px;position:relative;">
            <div class="av-media-box" style="position:relative;margin-bottom:8px;border-radius:10px;overflow:hidden;height:150px;background:#111;cursor:pointer;">
              <img src="${poster}" style="width:100%;height:100%;object-fit:cover;object-position:top center;position:absolute;inset:0;z-index:1;" alt="">
              ${isReady ? `
              <button class="av-gen-play-btn" type="button" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(168,85,247,0.85);border:none;color:white;border-radius:50%;width:44px;height:44px;display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:3;box-shadow:0 4px 12px rgba(0,0,0,0.4);"><i class="bi bi-play-fill" style="font-size:1.4rem;"></i></button>` : ''}
              <div style="position:absolute;top:7px;left:7px;z-index:4;">${badge}</div>
            </div>
            <div>
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:3px;">
                <h4 style="font-size:0.85rem;font-weight:600;margin:0;color:white;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(item.title||'Creación')}</h4>
                ${modeTag}
              </div>
              <p style="font-size:0.74rem;color:#71717a;margin:0 0 7px 0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(item.prompt||'')}</p>
              <div style="display:flex;align-items:center;justify-content:space-between;font-size:0.72rem;color:#52525b;">
                <span><i class="bi bi-clock"></i> ${formatTime(item.createdAt)}</span>
                <div style="display:flex;gap:5px;">
                  ${isReady ? `<a href="${itemUrl}" download target="_blank" style="color:white;background:rgba(255,255,255,0.1);padding:3px 8px;border-radius:5px;text-decoration:none;"><i class="bi bi-download"></i></a>` : ''}
                  <button class="av-all-del-btn" data-id="${item.id}" type="button" style="color:#ef4444;background:rgba(239,68,68,0.15);border:none;padding:3px 8px;border-radius:5px;cursor:pointer;"><i class="bi bi-trash"></i></button>
                </div>
              </div>
            </div>
          </div>`;
      }).join('');

      // Eventos play: abrir modal reproductor al hacer clic en la tarjeta o botón play
      qsa('.av-gen-card', container).forEach(card => {
        const mediaBox = card.querySelector('.av-media-box');
        const url = card.dataset.url;
        const title = card.dataset.title || 'Vídeo de Avatar';

        if (mediaBox && url) {
          mediaBox.addEventListener('click', (e) => {
            if (e.target.closest('.av-all-del-btn') || e.target.closest('a[download]')) return;
            openAvatarPreviewModal(url, title);
          });
        }
      });

    } catch (err) {
      console.error('[ALL CREACIONES]', err);
      container.innerHTML = `<div style="padding:20px;text-align:center;color:#ef4444;">Error al cargar. Comprueba tu conexión.</div>`;
    }
  }

  // Botón Actualizar del panel Mis Creaciones
  on(document, 'click', '#btnRefreshCreaciones', async () => {
    toast({ message: '🔄 Actualizando creaciones...', type: 'info' });
    await loadRecentAvatarGenerations();
    toast({ message: '✅ Creaciones actualizadas', type: 'success' });
  });


  // ── Drop zone foto a vídeo ────────────────────────────────
  const avFotoDrop        = qs('#avFotoDrop');
  const avFotoDropEmpty   = qs('#avFotoDropEmpty');
  const avFotoPreviewBox  = qs('#avFotoPreviewBox');
  const avFotoPreviewImg  = qs('#avFotoPreviewImg');
  const avFotoFileName    = qs('#avFotoFileName');
  let currentUploadedPhotoFile = null;

  async function setUploadedPhoto(file) {
    if (!file) return;
    currentUploadedPhotoFile = file;

    let localDataUrl = '';
    const reader = new FileReader();
    reader.onload = async (e) => {
      localDataUrl = e.target.result;
      if (avFotoPreviewImg) avFotoPreviewImg.src = localDataUrl;
      if (avFotoFileName) avFotoFileName.textContent = file.name;
      if (avFotoDropEmpty) avFotoDropEmpty.classList.add('is-hidden');
      if (avFotoPreviewBox) avFotoPreviewBox.classList.remove('is-hidden');
      if (avFotoDrop) avFotoDrop.classList.add('has-photo');

      // Crear e insertar la tarjeta de la foto subida directamente en la posición #2
      addNewAvatarToGrid(localDataUrl, file.name, false, true);
    };
    reader.readAsDataURL(file);

    try {
      const uploadRes = await uploadAttachment(file);
      const photoUrl = uploadRes.url || localDataUrl;
      currentUploadedPhotoFile._url = photoUrl;
      
      // Actualizar dataset y src de la tarjeta seleccionada con la URL final del servidor
      const selectedCard = qs('.av-thumb-card.is-selected');
      if (selectedCard && photoUrl) {
        selectedCard.dataset.avatar = photoUrl;
        const img = qs('img', selectedCard);
        if (img) img.src = photoUrl;
      }
      
      toast({ message: `Foto "${file.name}" subida y añadida correctamente`, type: 'success' });
    } catch (err) {
      console.error('Error enviando archivo al servidor:', err);
      toast({ message: `Foto "${file.name}" lista`, type: 'success' });
    }
  }

  function clearUploadedPhoto() {
    currentUploadedPhotoFile = null;
    if (avFotoPreviewImg) avFotoPreviewImg.src = '';
    if (avFotoFileName) avFotoFileName.textContent = '';
    if (avFotoDropEmpty) avFotoDropEmpty.classList.remove('is-hidden');
    if (avFotoPreviewBox) avFotoPreviewBox.classList.add('is-hidden');
    if (avFotoDrop) avFotoDrop.classList.remove('has-photo');
    toast({ message: 'Foto eliminada', type: 'info' });
  }

  if (avFotoDrop) {
    avFotoDrop.addEventListener('click', (e) => {
      if (e.target.closest('.av-foto-btn-action')) return;
      if (!currentUploadedPhotoFile) pickAvatarFoto();
    });
    avFotoDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      avFotoDrop.classList.add('is-dragover');
    });
    avFotoDrop.addEventListener('dragleave', () => avFotoDrop.classList.remove('is-dragover'));
    avFotoDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      avFotoDrop.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        setUploadedPhoto(file);
      } else if (file) {
        toast({ message: 'Por favor sube un archivo de imagen (PNG, JPG, WebP)', type: 'error' });
      }
    });
  }

  on(document, 'click', '#btnChangeFoto', (e) => {
    e.stopPropagation();
    pickAvatarFoto();
  });

  on(document, 'click', '#btnRemoveFoto', (e) => {
    e.stopPropagation();
    clearUploadedPhoto();
  });

  on(document, 'click', '#btnUploadAvatarCard', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setUploadedPhoto(file);
      }
    };
    input.click();
  });

  on(document, 'click', '.av-thumb-del-btn', async (e, btn) => {
    e.stopPropagation();
    e.preventDefault();
    const card = btn.closest('.av-thumb-card');
    if (!card) return;

    const confirmed = await showConfirmModal({
      title: '¿Eliminar foto de avatar?',
      message: '¿Estás seguro de que deseas eliminar esta foto de tu lista de avatares?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar'
    });

    if (confirmed) {
      const avatarUrl = card.dataset.avatar;
      card.remove();

      if (avatarUrl) {
        try {
          const saved = JSON.parse(localStorage.getItem('vimo_custom_avatars') || '[]');
          const updated = saved.filter(item => item.url !== avatarUrl && item.name !== avatarUrl && item.id !== avatarUrl);
          localStorage.setItem('vimo_custom_avatars', JSON.stringify(updated));

          const deletedStatic = JSON.parse(localStorage.getItem('vimo_deleted_static_cards') || '[]');
          if (!deletedStatic.includes(avatarUrl)) {
            deletedStatic.push(avatarUrl);
            localStorage.setItem('vimo_deleted_static_cards', JSON.stringify(deletedStatic));
          }
        } catch {}
      }

      toast({ message: 'Foto eliminada correctamente', type: 'success' });
    }
  });

  // ── Conmutador de subpestañas: Todos / Catálogo de Avatares / Mis Fotos Subidas / Mis Creaciones Recientes ──
  function filterAvatarCards(subtab) {
    qsa('.av-subnav-btn').forEach(b => {
      const active = b.dataset.subtab === subtab;
      b.classList.toggle('is-active', active);
      b.style.background = active ? 'rgba(168,85,247,0.18)' : 'rgba(255,255,255,0.05)';
      b.style.borderColor = active ? 'rgba(168,85,247,0.5)' : 'rgba(255,255,255,0.1)';
      b.style.color = active ? '#fff' : '#94a3b8';
    });

    const gridEl = qs('#avFotoThumbGrid');
    const recentSec = qs('#avRecentGenerationsSection');

    // La sección "Mis creaciones de avatar recientes" SOLO es visible en la pestaña 'mis-creaciones'
    if (recentSec) {
      recentSec.style.display = (subtab === 'mis-creaciones') ? 'block' : 'none';
      if (subtab === 'mis-creaciones') {
        recentSec.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    if (subtab === 'mis-creaciones') {
      if (gridEl) gridEl.style.display = 'none';
      return;
    }

    if (gridEl) gridEl.style.display = 'grid';

    const isCatalogPreset = (av) => ['annie','shawn','sofia','david','clara','marcos','nadia','denise','leo'].includes(av);

    // Ocultar tarjetas eliminadas previamente
    let deletedStatic = [];
    try {
      deletedStatic = JSON.parse(localStorage.getItem('vimo_deleted_static_cards') || '[]');
    } catch {}

    qsa('.av-thumb-card').forEach(card => {
      const av = card.dataset.avatar || '';
      if (deletedStatic.includes(av)) {
        card.remove();
        return;
      }

      // El botón "Subir nueva foto" SOLO se muestra en la pestaña 'mis-fotos'
      if (card.id === 'btnUploadAvatarCard') {
        card.style.display = (subtab === 'mis-fotos') ? 'flex' : 'none';
        return;
      }

      const type = card.dataset.type || (isCatalogPreset(av) ? 'catalogo' : 'mis-fotos');

      if (subtab === 'todos') {
        card.style.display = 'flex';
      } else if (subtab === 'catalogo') {
        card.style.display = (type === 'catalogo' || isCatalogPreset(av)) ? 'flex' : 'none';
      } else if (subtab === 'mis-fotos') {
        card.style.display = (type === 'mis-fotos' && !isCatalogPreset(av)) ? 'flex' : 'none';
      }
    });
  }

  async function loadAvatarCatalogFromDb() {
    const grid = qs('#avFotoThumbGrid');
    if (!grid) return;

    try {
      const avatars = await apiRequest('/avatars');
      if (Array.isArray(avatars) && avatars.length > 0) {
        const uploadCardsMap = new Map();
        qsa('.av-thumb-card[data-type="subida"]', grid).forEach(c => {
          const url = c.dataset.avatar;
          if (url && !uploadCardsMap.has(url)) {
            uploadCardsMap.set(url, c.outerHTML);
          }
        });

        let html = avatars.map((av, index) => `
          <div class="av-thumb-card ${index === 0 ? 'is-selected' : ''}" role="button" tabindex="0" data-avatar="${av.slug}" data-type="catalogo">
            <img class="av-thumb-img" src="${sanitizeMediaUrl(av.photoUrl)}" alt="${av.name}" loading="lazy">
            <span class="av-thumb-name">${av.name}</span>
          </div>
        `).join('');

        uploadCardsMap.forEach(cardHtml => {
          html += cardHtml;
        });

        html += `
          <div class="av-thumb-card av-thumb-upload-btn" id="btnUploadAvatarCard" role="button" tabindex="0" style="display:none; flex-direction:column; align-items:center; justify-content:center; background:rgba(168,85,247,0.08); border:2px dashed rgba(168,85,247,0.4); text-align:center; padding:16px;">
            <div style="width:40px; height:40px; border-radius:50%; background:rgba(168,85,247,0.2); color:#c084fc; display:flex; align-items:center; justify-content:center; font-size:1.3rem; margin-bottom:8px;">
              <i class="bi bi-cloud-arrow-up-fill"></i>
            </div>
            <strong style="color:#ffffff; font-size:0.9rem; margin-bottom:2px;">Subir nueva foto</strong>
            <span style="color:#94a3b8; font-size:0.75rem;">Añade tu rostro</span>
          </div>
        `;

        grid.innerHTML = html;
        console.log(`[AVATARS] Cargados ${avatars.length} avatares dinámicos desde la BD.`);
      }
    } catch (err) {
      console.warn('[AVATARS] Error cargando catálogo desde BD, usando grid local:', err.message);
    }
  }

  // Cargar catálogo desde BD al iniciar
  loadAvatarCatalogFromDb().then(() => {
    filterAvatarCards('todos');
  });

  on(document, 'click', '.av-subnav-btn', (e, btn) => {
    filterAvatarCards(btn.dataset.subtab || 'todos');
  });

  // Selección de tarjeta de avatar al hacer clic
  on(document, 'click', '.av-thumb-card', (e, card) => {
    if (e.target.closest('.av-thumb-del-btn') || card.id === 'btnUploadAvatarCard') return;
    qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');
  });

  // ── Botón Generar Vídeo de Avatar ─────────────────────────
  let genCountNum = 2;

  // ── Gestión persistente de tarjetas eliminadas ──────────────
  function getDeletedCardIds() {
    try {
      return JSON.parse(localStorage.getItem('vimo_deleted_generations') || '[]');
    } catch {
      return [];
    }
  }

  function markCardAsDeleted(id) {
    if (!id) return;
    const deleted = getDeletedCardIds();
    if (!deleted.includes(id)) {
      deleted.push(id);
      localStorage.setItem('vimo_deleted_generations', JSON.stringify(deleted));
    }
    apiRequest(`/generations/${id}`, { method: 'DELETE' }).catch(() => {});
  }

  // Evento para ELIMINAR una creación reciente de avatar al pulsar la papelera
  on(document, 'click', '.av-gen-card .btn-del', async (e, btn) => {
    e.stopPropagation();
    e.preventDefault();
    const card = btn.closest('.av-gen-card');
    if (!card) return;

    const confirmed = await showConfirmModal({
      title: '¿Eliminar creación de avatar?',
      message: '¿Estás seguro de que deseas eliminar este vídeo de avatar?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar'
    });

    if (confirmed) {
      const id = card.dataset.id;
      if (id) markCardAsDeleted(id);
      card.remove();
      toast({ message: 'Creación de avatar eliminada correctamente', type: 'success' });
    }
  });

  // Evento para DESCARGAR el vídeo MP4 de una creación reciente
  on(document, 'click', '.av-gen-card .btn-dl', (e, btn) => {
    e.stopPropagation();
    e.preventDefault();
    const card = btn.closest('.av-gen-card');
    if (!card) return;
    const video = qs('.av-gen-video', card);
    const src = video ? (video.currentSrc || video.src) : null;
    if (src) {
      const a = document.createElement('a');
      a.href = src;
      a.download = `avatar-video-${card.dataset.id || 'creacion'}.mp4`;
      a.target = '_blank';
      a.click();
    } else {
      toast({ message: 'El vídeo aún no está listo para descargar', type: 'info' });
    }
  });

  // ── Referencia a la lista de generaciones y a la función constructora de tarjetas ──
  const genList = qs('#avFotoGenerationsList');
  const countEl = qs('#avGenCount');

  // Construye el elemento HTML de una tarjeta de generación de avatar
  function buildAvatarCard(gen) {
    const card = document.createElement('div');
    card.className = 'av-gen-card';
    card.dataset.id = gen.id || '';

    // Poster: para tarjetas en curso mostramos la foto que subió el usuario
    const userPhotoThumb = gen.thumb || gen.meta?.photoUrl || '';
    const videoUrl = gen.url || gen.resultUrl || '';
    const isCompleted = gen.status === 'completed';
    const isQueued = gen.status === 'queued' || gen.status === 'processing';

    // Solo mostrar el vídeo si está completado Y tiene URL de vídeo real (mp4/webm/fal-cdn)
    const hasRealVideo = isCompleted && videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('fal.media') || videoUrl.includes('cdn'));
    const mediaHtml = hasRealVideo
      ? `<video class="av-gen-video" src="${videoUrl}" poster="${userPhotoThumb || uploadsUrl + '/demo_avatar_woman.png'}" preload="metadata" playsinline></video>`
      : `<img class="av-gen-video" src="${userPhotoThumb || (uploadsUrl + '/demo_avatar_woman.png')}" alt="Avatar" style="object-fit:cover;width:100%;height:100%;border-radius:8px 8px 0 0;">`;

    const badgeHtml = isCompleted && hasRealVideo
      ? `<span class="av-gen-badge-status"><i class="bi bi-check2-circle"></i> Completado</span>`
      : isQueued || (isCompleted && !hasRealVideo)
        ? `<span class="av-gen-badge-status" style="background:#b45309"><i class="bi bi-hourglass-split"></i> Procesando...</span>`
        : `<span class="av-gen-badge-status" style="background:#991b1b"><i class="bi bi-x-circle"></i> Error</span>`;

    const dateLabel = gen.createdAt ? new Date(gen.createdAt).toLocaleString('es-ES', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : 'Ahora mismo';
    const promptText = gen.prompt || gen.title || '';

    card.dataset.id = gen.id || '';
    card.dataset.jobId = gen.jobId || '';
    card.dataset.status = gen.status;

    card.innerHTML = `
      <div class="av-gen-media" style="position:relative;">
        ${mediaHtml}
        ${hasRealVideo ? `<button class="av-gen-play-btn" type="button" aria-label="Reproducir"><i class="bi bi-play-fill"></i></button>` : ''}
        ${badgeHtml}
        <span class="av-gen-duration">00:18</span>
        <div class="av-gen-actions-overlay">
          <button class="av-gen-act-btn btn-dl" type="button" title="Descargar MP4">
            <i class="bi bi-download"></i>
          </button>
          <button class="av-gen-act-btn btn-del" type="button" title="Eliminar">
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      </div>
      <div class="av-gen-details">
        <h4 class="av-gen-name">Avatar — ${promptText.slice(0, 30) || 'Sin guión'}</h4>
        <p class="av-gen-script">"${promptText.slice(0, 80)}${promptText.length > 80 ? '...' : ''}"</p>
        <div class="av-gen-foot">
          <span class="av-gen-date"><i class="bi bi-clock"></i> ${dateLabel}</span>
          <div class="av-gen-actions">
            <button class="av-gen-act-btn btn-dl" type="button" title="Descargar MP4">
              <i class="bi bi-download"></i>
            </button>
            <button class="av-gen-act-btn btn-del" type="button" title="Eliminar">
              <i class="bi bi-trash3"></i>
            </button>
          </div>
        </div>
      </div>
    `;
    return card;
  }

  // Polling activo para tarjetas en curso
  const pollingIntervals = new Map(); // genId → intervalId

  function startPollingCard(gen) {
    if (pollingIntervals.has(gen.id)) return; // ya haciendo polling
    const jobId = gen.jobId;
    if (!jobId) return;

    console.log(`[AVATAR POLL] Iniciando polling para job ${jobId}`);
    const intervalId = setInterval(async () => {
      try {
        const jobStatus = await getJobStatus(jobId);
        console.log(`[AVATAR POLL] Job ${jobId}: status=${jobStatus.status}, url=${jobStatus.result?.url}`);

        if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
          clearInterval(intervalId);
          pollingIntervals.delete(gen.id);
          // Recargar toda la lista para mostrar el resultado final
          await loadAvatarGenerations();
        }
      } catch (err) {
        console.warn(`[AVATAR POLL] Error polling job ${jobId}:`, err.message);
      }
    }, 10000); // cada 10 segundos

    pollingIntervals.set(gen.id, intervalId);
  }

  async function loadAvatarGenerations() {
    if (!genList) return;
    // Detener todos los pollings previos
    pollingIntervals.forEach((id) => clearInterval(id));
    pollingIntervals.clear();

    try {
      const data = await listHistory();
      const items = Array.isArray(data) ? data : (data?.generations || data?.items || []);
      const avatarItems = items.filter(g => g.type === 'avatar');
      const deletedIds = getDeletedCardIds();

      // Limpiar cards anteriores
      genList.innerHTML = '';

      if (avatarItems.length === 0) {
        genList.innerHTML = '<p style="color:var(--text-muted,#888);padding:1rem;text-align:center">Aún no has creado ningún avatar. ¡Sube una foto y genera tu primer vídeo!</p>';
        if (countEl) countEl.textContent = '0 vídeos';
        return;
      }

      let count = 0;
      for (const gen of avatarItems) {
        if (deletedIds.includes(gen.id)) continue;
        const card = buildAvatarCard(gen);
        genList.appendChild(card);
        count++;
        // Si está en proceso, iniciar polling
        if (gen.status === 'queued' || gen.status === 'processing') {
          startPollingCard(gen);
        }
      }
      if (countEl) countEl.textContent = `${count} vídeos`;
    } catch (err) {
      console.warn('[AVATAR] Error cargando generaciones:', err.message);
    }
  }

    // Cargar generaciones al iniciar la página
  loadAvatarGenerations();

  // Acciones en tarjetas de generaciones recientes
  on(document, 'click', '.av-gen-act-btn.btn-del, .btn-del-avatar-gen', async (e, btn) => {
    e.stopPropagation();
    e.preventDefault();
    const card = btn.closest('.av-gen-card') || btn.closest('.gen-card');
    if (!card) return;
    const cardId = card.dataset.id || card.getAttribute('data-id');

    const confirmed = await showConfirmModal({
      title: '¿Eliminar avatar?',
      message: '¿Estás seguro de que deseas eliminar esta creación de avatar?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar'
    });

    if (confirmed) {
      try {
        if (cardId) {
          const { deleteGeneration } = await import('./api.js');
          await deleteGeneration(cardId);
          markCardAsDeleted(cardId);
        }
        card.remove();
        toast({ message: 'Avatar eliminado correctamente', type: 'success' });
        const list = qs('#avFotoGenerationsList') || qs('#avRecentGenerationsList');
        if (list) {
          const remaining = qsa('.av-gen-card', list).length;
          const countEl = qs('#avGenCount');
          if (countEl) countEl.textContent = `${remaining} vídeos`;
        }
      } catch (err) {
        console.error('[AVATAR] Error al eliminar:', err);
        card.remove();
      }
    }
  });

  on(document, 'click', '.av-gen-act-btn.btn-dl', (e, btn) => {
    e.stopPropagation();
    const card = btn.closest('.av-gen-card');
    const video = qs('video', card);
    const videoUrl = video?.src || `${uploadsUrl}/demo_avatar_woman.webm`;
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `vimo-avatar-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({ message: 'Descargando vídeo MP4...', type: 'success' });
  });



  function pickAvatarFoto() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) setUploadedPhoto(file);
    });
    input.click();
  }

  // ── Contador de caracteres (formato X/840) ────────────────
  const MAX_CHARS = 840;

  function updateCount() {
    if (scriptCount) scriptCount.textContent = scriptArea?.value.length ?? 0;
  }

  scriptArea?.addEventListener('input', updateCount);

  // ── Posicionamiento del tooltip de la bombilla ────────────
  const hintWrap    = qs('.avatar-hint-wrap');
  const hintTooltip = qs('.avatar-hint-tooltip');

  if (hintWrap && hintTooltip) {
    hintWrap.addEventListener('mouseenter', () => {
      const rect = hintWrap.getBoundingClientRect();
      hintTooltip.style.left = `${rect.left}px`;
      hintTooltip.style.top  = `${rect.top - hintTooltip.offsetHeight - 10}px`;
    });
    // Recalcula si el tooltip aún no tiene altura en el primer hover
    hintWrap.addEventListener('mouseover', () => {
      const rect = hintWrap.getBoundingClientRect();
      hintTooltip.style.left = `${rect.left}px`;
      hintTooltip.style.top  = `${rect.top - hintTooltip.offsetHeight - 10}px`;
    });
  }

  // ── Guion de muestra ──────────────────────────────────────
  const SAMPLE_SCRIPT = `Nunca dejes que el miedo al fracaso te impida intentarlo, porque cada intento te acerca un paso más a tu próxima gran victoria.

Llevamos años queriendo compartir lo que sabemos. Enseñar. Inspirar. Ayudar a otros a crecer más rápido.

Pero crear contenido de calidad lleva una eternidad. Grabar. Editar. Repetir. Es agotador.

Hasta que descubrí una forma diferente de hacerlo.

Con inteligencia artificial, puedo convertir un guion en un vídeo profesional en minutos, sin estudio, sin cámaras, sin complicaciones.

Puedo hablar en cualquier idioma, actualizar mi mensaje al instante y llegar a más personas que nunca.

¿Y lo mejor? Mi voz, mi imagen, mi esencia — siempre presentes.`;

  on(document, 'click', '[data-action="insert-sample-script"]', () => {
    if (!scriptArea) return;
    scriptArea.value = SAMPLE_SCRIPT;
    updateCount();
    scriptArea.focus();
  });

  // ── Selección de avatar y reproducción/pausa de vista previa ─────────
  // Pausar cualquier vídeo de avatar al iniciar la página
  qsa('.av-thumb-card video').forEach(v => {
    try {
      v.pause();
      v.currentTime = 0;
    } catch {}
  });

  on(document, 'click', '.av-thumb-card', (e, card) => {
    qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    const video = qs('video', card);
    const playOverlayIcon = qs('.av-thumb-play-overlay i', card);

    if (video) {
      // Pausar otros vídeos de las miniatura de avatares
      qsa('.av-thumb-card video').forEach(otherVideo => {
        if (otherVideo !== video && !otherVideo.paused) {
          otherVideo.pause();
          const otherIcon = qs('.av-thumb-play-overlay i', otherVideo.closest('.av-thumb-card'));
          if (otherIcon) otherIcon.className = 'bi bi-play-fill';
        }
      });

      if (video.paused) {
        video.play().then(() => {
          if (playOverlayIcon) playOverlayIcon.className = 'bi bi-pause-fill';
          toast({ message: 'Reproduciendo avatar...', type: 'info' });
        }).catch(err => {
          console.error('Error playing avatar preview video:', err);
        });
      } else {
        video.pause();
        if (playOverlayIcon) playOverlayIcon.className = 'bi bi-play-fill';
      }
    }
  });

  // ── Selector de modelo ────────────────────────────────────
  on(document, 'click', '.av-model-item', (e, item) => {
    qsa('.av-model-item').forEach(i => i.classList.remove('is-active'));
    item.classList.add('is-active');

    const badge = item.dataset.badge;
    const name  = qs('strong', item)?.textContent || 'Avatar';
    const isNew = item.dataset.new === 'true';

    const badgeEl = qs('[data-bind="av-model-badge"]');
    const nameEl  = qs('[data-bind="av-model-name"]');
    const newBadge = qs('.avatar-pill .badge-new');

    if (badgeEl) badgeEl.textContent = badge;
    if (nameEl)  nameEl.textContent  = name;
    if (newBadge) newBadge.style.display = isNew ? '' : 'none';
  });



  // ── Modal Clóname (grabar / subir vídeo) ─────────────────
  // Estado de la grabación de cámara
  const cam = {
    stream:        null,        // MediaStream activo
    recorder:      null,        // MediaRecorder
    chunks:        [],          // Trozos de vídeo grabados
    recordedBlob:  null,        // Blob final
    countdownInt:  null,        // Intervalo del temporizador
    selectedCamId: null,
    selectedMicId: null,
    durationSec:   15,          // duración objetivo (15 s)
  };

  function openClonamodal(view = 'grabar') {
    clonamodalRoot?.classList.add('is-open');
    clonamodalRoot?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    switchClonamodalView(view);

    // Si abrimos en modo "grabar", solicitamos permisos de cámara
    if (view === 'grabar') requestCameraAccess();
  }

  function closeClonamodal() {
    clonamodalRoot?.classList.remove('is-open');
    clonamodalRoot?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    stopCameraStream();
  }

  function switchClonamodalView(view) {
    qsa('.cloname-view').forEach(v =>
      v.classList.toggle('is-hidden', v.dataset.clonameView !== view)
    );
  }

  // ── Cámara + grabación (WebRTC) ───────────────────────────
  function setCamState(state) {
    qsa('.cloname-state').forEach(el =>
      el.classList.toggle('is-hidden', el.dataset.camState !== state)
    );
  }

  async function requestCameraAccess() {
    // Comprobaciones previas
    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraError('Navegador no compatible',
        'Tu navegador no soporta acceso a cámara. Prueba con Chrome, Safari o Firefox.',
        'Error: MediaDevicesNotSupported');
      return;
    }

    setCamState('loading');

    try {
      // Pedimos vídeo + audio. facingMode:'user' = cámara frontal (clave en móvil).
      const constraints = {
        video: cam.selectedCamId
          ? { deviceId: { exact: cam.selectedCamId } }
          : { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: cam.selectedMicId
          ? { deviceId: { exact: cam.selectedMicId } }
          : true,
      };

      stopCameraStream();   // por si había uno previo
      cam.stream = await navigator.mediaDevices.getUserMedia(constraints);

      const video = qs('#clonameVideo');
      if (video) {
        video.srcObject = cam.stream;
        await video.play().catch(() => { /* ignora autoplay errors */ });
      }
      setCamState('live');

      // Una vez con permisos podemos enumerar dispositivos con labels
      populateDeviceList();
    } catch (err) {
      handleCameraError(err);
    }
  }

  function handleCameraError(err) {
    let title = 'Dispositivo no autorizado';
    let desc  = 'Permite que el navegador acceda a tu cámara y micrófono y actualiza la página';

    switch (err?.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        title = 'Permiso denegado';
        desc  = 'Has bloqueado el acceso a la cámara. Ábrelo desde los ajustes del navegador y vuelve a intentarlo.';
        break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        title = 'Sin dispositivo';
        desc  = 'No hemos encontrado ninguna cámara conectada.';
        break;
      case 'NotReadableError':
      case 'TrackStartError':
        title = 'Cámara ocupada';
        desc  = 'Otra aplicación está usando tu cámara. Ciérrala y vuelve a intentarlo.';
        break;
      case 'OverconstrainedError':
        title = 'Configuración no soportada';
        desc  = 'Tu cámara no admite la configuración solicitada.';
        break;
    }
    showCameraError(title, desc, `Error: ${err?.name || 'Unknown'}`);
  }

  function showCameraError(title, desc, code) {
    const t = qs('#clonameErrorTitle');
    const d = qs('#clonameErrorDesc');
    const c = qs('#clonameErrorCode');
    if (t) t.textContent = title;
    if (d) d.innerHTML = `${desc}<br><span class="cloname-error-code" id="clonameErrorCode">${code}</span>`;
    if (c) c.textContent = code;
    setCamState('error');
  }

  function stopCameraStream() {
    if (cam.stream) {
      cam.stream.getTracks().forEach(t => t.stop());
      cam.stream = null;
    }
    if (cam.recorder && cam.recorder.state !== 'inactive') {
      try { cam.recorder.stop(); } catch { /* noop */ }
    }
    clearInterval(cam.countdownInt);
    cam.countdownInt = null;
  }

  async function populateDeviceList() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === 'videoinput');
      const mics = devices.filter(d => d.kind === 'audioinput');

      fillDeviceMenu('#clonameCamMenu', '#clonameCamLabel', cams, 'cam');
      fillDeviceMenu('#clonameMicMenu', '#clonameMicLabel', mics, 'mic');
    } catch (e) {
      // silencioso: si falla, los selectores se quedan con su placeholder
    }
  }

  function fillDeviceMenu(menuSel, labelSel, devices, kind) {
    const menu  = qs(menuSel);
    const label = qs(labelSel);
    if (!menu) return;

    if (!devices.length) {
      menu.innerHTML = `<li><button class="cloname-lang-item" type="button" disabled>Sin dispositivos</button></li>`;
      if (label) label.textContent = kind === 'cam' ? 'Cámara' : 'Micrófono';
      return;
    }

    // El primero queda como activo por defecto
    const activeId = kind === 'cam' ? (cam.selectedCamId || devices[0].deviceId)
                                    : (cam.selectedMicId || devices[0].deviceId);
    if (kind === 'cam') cam.selectedCamId = activeId;
    else                cam.selectedMicId = activeId;

    menu.innerHTML = devices.map((d, i) => `
      <li>
        <button class="cloname-lang-item ${d.deviceId === activeId ? 'is-active' : ''}"
          type="button" data-device-id="${d.deviceId}" data-kind="${kind}">
          ${escapeHtml(d.label || `${kind === 'cam' ? 'Cámara' : 'Micrófono'} ${i + 1}`)}
        </button>
      </li>
    `).join('');

    if (label) {
      const active = devices.find(d => d.deviceId === activeId);
      label.textContent = (active?.label || (kind === 'cam' ? 'Cámara' : 'Micrófono'))
        .replace(/\s\(.*\)/, '');   // recorta "(0a23:abcd)" largo
    }
  }

  // Selección manual de dispositivo desde el dropdown
  on(document, 'click', '.cloname-lang-item[data-device-id]', (e, btn) => {
    const kind = btn.dataset.kind;
    const id   = btn.dataset.deviceId;
    if (kind === 'cam') cam.selectedCamId = id;
    else                cam.selectedMicId = id;
    requestCameraAccess();
  });

  // Reintentar acceso a cámara
  on(document, 'click', '[data-action="retry-camera"]', requestCameraAccess);

  // Volver a grabar
  on(document, 'click', '[data-action="retake-camera"]', () => {
    cam.recordedBlob = null;
    requestCameraAccess();
  });

  // ── Grabación con MediaRecorder ───────────────────────────
  function pickMimeType() {
    const candidates = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',                  // Safari iOS/macOS
    ];
    return candidates.find(t => window.MediaRecorder?.isTypeSupported?.(t)) || '';
  }

  function startRecording() {
    if (!cam.stream || !window.MediaRecorder) {
      toast({ message: 'La grabación no está disponible en este dispositivo', type: 'error' });
      return;
    }
    cam.chunks = [];
    const mimeType = pickMimeType();

    try {
      cam.recorder = new MediaRecorder(cam.stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      toast({ message: 'No se pudo iniciar la grabación', type: 'error' });
      return;
    }

    cam.recorder.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size > 0) cam.chunks.push(ev.data);
    });

    cam.recorder.addEventListener('stop', () => {
      const blob = new Blob(cam.chunks, { type: mimeType || 'video/webm' });
      cam.recordedBlob = blob;
      showReview(blob);
    });

    cam.recorder.start();

    // UI de grabación + temporizador descendente
    const recBtn  = qs('#clonameRecBtn');
    const recInd  = qs('#clonameRecIndicator');
    const timerEl = qs('#clonameRecTimer');
    recBtn?.classList.add('is-recording');
    recBtn?.setAttribute('aria-label', 'Detener grabación');
    recInd?.classList.remove('is-hidden');

    let remaining = cam.durationSec;
    const updateTimer = () => {
      const m = String(Math.floor(remaining / 60)).padStart(2, '0');
      const s = String(remaining % 60).padStart(2, '0');
      if (timerEl) timerEl.textContent = `${m}:${s}`;
    };
    updateTimer();

    cam.countdownInt = setInterval(() => {
      remaining--;
      updateTimer();
      if (remaining <= 0) stopRecording();
    }, 1000);
  }

  function stopRecording() {
    clearInterval(cam.countdownInt);
    cam.countdownInt = null;
    if (cam.recorder && cam.recorder.state !== 'inactive') {
      cam.recorder.stop();
    }
    const recBtn = qs('#clonameRecBtn');
    const recInd = qs('#clonameRecIndicator');
    recBtn?.classList.remove('is-recording');
    recBtn?.setAttribute('aria-label', 'Iniciar grabación');
    recInd?.classList.add('is-hidden');
  }

  function showReview(blob) {
    const url = URL.createObjectURL(blob);
    const review = qs('#clonameReview');
    if (review) {
      review.src = url;
      review.load();
    }
    // Detenemos el stream en vivo para liberar la cámara durante la revisión
    if (cam.stream) {
      cam.stream.getTracks().forEach(t => t.stop());
      cam.stream = null;
    }
    setCamState('review');
  }

  // Toggle grabar/parar al pulsar el botón circular
  on(document, 'click', '#clonameRecBtn', () => {
    const isRec = qs('#clonameRecBtn')?.classList.contains('is-recording');
    if (isRec) stopRecording();
    else       startRecording();
  });

  // Al pulsar "Listo" usamos el blob (si lo hay) para subirlo
  on(document, 'click', '.btn-cloname-done', async () => {
    if (!cam.recordedBlob) {
      toast({ message: 'Graba un vídeo antes de continuar', type: 'error' });
      return;
    }
    const btn = qs('.btn-cloname-done');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Procesando...';
    }
    toast({ message: 'Subiendo grabación de avatar…', type: 'info' });
    try {
      const file = new File([cam.recordedBlob], 'clon-avatar.webm', { type: cam.recordedBlob.type });
      const result = await uploadAttachment(file);
      addNewAvatarToGrid(result.url, result.name, true);
      toast({ message: 'Avatar clonado y seleccionado', type: 'success' });
      closeClonamodal();
    } catch (err) {
      toast({ message: `Error al subir grabación: ${err.message}`, type: 'error' });
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Listo';
      }
    }
  });

  // Helper de escape (evita XSS al pintar labels de dispositivos)
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Abrir desde "Clóname" (vista grabar)
  on(document, 'click', '.av-action-clone', () => openClonamodal('grabar'));

  // Abrir desde "Crear un avatar" (vista subir directamente)
  on(document, 'click', '.av-action-create', () => openClonamodal('subir'));

  // Cerrar
  on(document, 'click', '[data-action="close-cloname-modal"]', closeClonamodal);
  clonamodalRoot?.addEventListener('click', (e) => {
    if (e.target === clonamodalRoot) closeClonamodal();
  });

  // "Subir grabaciones" — cambia a la vista de subida
  on(document, 'click', '[data-action="cloname-switch-upload"]', () => switchClonamodalView('subir'));

  // "Atrás" desde la vista de subida — vuelve a grabar
  on(document, 'click', '[data-action="cloname-switch-grabar"]', () => switchClonamodalView('grabar'));

  // Drop zone de vídeo
  const clonameDrop = qs('#clonamodalDrop');
  if (clonameDrop) {
    clonameDrop.addEventListener('click', (e) => {
      if (e.target.closest('.cloname-gdrive-btn') || e.target.closest('.cloname-gdrive-menu')) return;
      pickClonamodalVideo();
    });
    clonameDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      clonameDrop.classList.add('is-dragover');
    });
    clonameDrop.addEventListener('dragleave', () => clonameDrop.classList.remove('is-dragover'));
    clonameDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      clonameDrop.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file) {
        clonameUploadedFile = file;
        toast({ message: `Vídeo "${file.name}" cargado`, type: 'success' });
        enableCreateBtn();
      }
    });
  }

  function pickClonamodalVideo() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm,video/quicktime,video/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      clonameUploadedFile = file;
      toast({ message: `Vídeo "${file.name}" cargado`, type: 'success' });
      enableCreateBtn();
    });
    input.click();
  }

  function enableCreateBtn() {
    const btn = qs('.btn-cloname-create');
    if (btn) btn.disabled = false;
  }

  on(document, 'click', '.btn-cloname-create', async () => {
    if (!clonameUploadedFile) return;
    const btn = qs('.btn-cloname-create');
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Creando...';
    }
    toast({ message: 'Subiendo y creando avatar de vídeo…', type: 'info' });
    try {
      const result = await uploadAttachment(clonameUploadedFile);
      addNewAvatarToGrid(result.url, result.name, true);
      toast({ message: 'Avatar de vídeo creado y seleccionado', type: 'success' });
      closeClonamodal();
    } catch (err) {
      toast({ message: `Error al crear avatar: ${err.message}`, type: 'error' });
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Crear avatar';
      }
    }
  });

  // ── Modal Clóname (foto) ──────────────────────────────────
  function openCloneModal() {
    cloneModal?.classList.add('is-open');
    cloneModal?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeCloneModal() {
    cloneModal?.classList.remove('is-open');
    cloneModal?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  on(document, 'click', '.av-action-photo', openCloneModal);
  on(document, 'click', '[data-action="close-clone-modal"]', closeCloneModal);

  cloneModal?.addEventListener('click', (e) => {
    if (e.target === cloneModal) closeCloneModal();
  });

  // File picker del botón "Seleccionar fotos" y clic en la drop zone
  on(document, 'click', '[data-action="clone-pick-photos"]', () => pickClonePhotos());

  const cloneDropZone = qs('#cloneDropZone');
  if (cloneDropZone) {
    cloneDropZone.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="clone-pick-photos"]')) return;
      pickClonePhotos();
    });
    cloneDropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      cloneDropZone.classList.add('is-dragover');
    });
    cloneDropZone.addEventListener('dragleave', () => cloneDropZone.classList.remove('is-dragover'));
    cloneDropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      cloneDropZone.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        handlePhotoUpload(file);
      }
    });
  }

  async function handlePhotoUpload(file) {
    toast({ message: `Subiendo foto "${file.name}"…`, type: 'info' });
    try {
      const result = await uploadAttachment(file);
      addNewAvatarToGrid(result.url, result.name, false);
      toast({ message: 'Foto de avatar subida y seleccionada', type: 'success' });
      closeCloneModal();
    } catch (err) {
      toast({ message: `Error al subir la foto: ${err.message}`, type: 'error' });
    }
  }

  function pickClonePhotos() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/heic,image/webp,image/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) handlePhotoUpload(file);
    });
    input.click();
  }

  // Escape cierra cualquier modal abierto (único listener global)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (createVoiceModal?.classList.contains('is-open')) closeCreateVoiceModal();
    if (clonamodalRoot?.classList.contains('is-open'))  closeClonamodal();
    if (cloneModal?.classList.contains('is-open'))      closeCloneModal();
    if (audioModal?.classList.contains('is-open'))      closeAudioModal();
    if (voiceModal?.classList.contains('is-open'))      closeVoiceModal();
  });

  // ── Estado global de Audio Grabado o Subido ────────────────
  let activeAudioTrack = null; // { url, name, isRecorded }
  let mediaRecorder = null;
  let audioStream = null;
  let audioChunks = [];
  let recordTimerInterval = null;
  let recordSeconds = 0;
  let recordedAudioBlob = null;
  let recordedAudioUrl = null;
  let uploadedAudioFile = null;

  // Actualizar lista de micrófonos disponibles
  async function updateAudioDeviceList() {
    const label = qs('#audioDeviceLabel');
    const select = qs('#audioDeviceSelect');
    if (!label) return;

    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      label.textContent = 'Micrófono predeterminado';
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      if (audioInputs.length > 0) {
        if (select) {
          select.innerHTML = audioInputs.map((d, i) => `
            <option value="${d.deviceId}">${d.label || `Micrófono ${i + 1}`}</option>
          `).join('');
        }
        const active = audioInputs.find(d => d.label) || audioInputs[0];
        label.textContent = active.label || `Micrófono (${audioInputs.length} disponible${audioInputs.length > 1 ? 's' : ''})`;
      } else {
        label.textContent = 'Micrófono predeterminado';
      }
    } catch (err) {
      console.warn('[AUDIO] Error detectando micrófonos:', err);
      label.textContent = 'Micrófono predeterminado';
    }
  }

  // ── Modal de audio ───────────────────────────────────────
  function openAudioModal(tab) {
    switchAudioTab(tab);
    updateAudioDeviceList();
    audioModal?.classList.add('is-open');
    audioModal?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeAudioModal() {
    audioModal?.classList.remove('is-open');
    audioModal?.setAttribute('aria-hidden', 'true');
    // Parar grabación si estaba activa al cerrar
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      stopAudioRecording(false);
    }
    document.body.style.overflow = '';
  }

  function switchAudioTab(tab) {
    const titles = { grabar: 'Grabar audio', subir: 'Subir audio' };
    if (audioModalTitle) audioModalTitle.textContent = titles[tab] || 'Audio';

    qsa('.audio-tab').forEach(t =>
      t.classList.toggle('is-active', t.dataset.audioTab === tab)
    );
    qsa('.audio-tabpanel').forEach(p =>
      p.classList.toggle('is-hidden', p.dataset.audioPanel !== tab)
    );
  }

  // Abrir desde los botones del header de guion
  on(document, 'click', '[data-action="open-audio-modal"]', (e, btn) => {
    openAudioModal(btn.dataset.audioTab || 'grabar');
  });

  // Cerrar con X
  on(document, 'click', '[data-action="close-audio-modal"]', closeAudioModal);

  // Cerrar con backdrop
  audioModal?.addEventListener('click', (e) => {
    if (e.target === audioModal) closeAudioModal();
  });

  // Cambio de pestañas dentro del modal
  on(document, 'click', '.audio-tab', (e, tab) => {
    switchAudioTab(tab.dataset.audioTab);
  });

  // Formato mm:ss
  function formatAudioTimer(secs) {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  // Iniciar grabación con MediaRecorder real
  async function startAudioRecording() {
    const btnRecord = qs('#btnRecord');
    const hint = qs('#audioRecordHint');
    const timerEl = qs('#audioRecordTimer');
    const previewBox = qs('#audioRecordPreviewBox');

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast({ message: '⚠️ Tu navegador no soporta grabación directa de audio.', type: 'error' });
      return;
    }

    try {
      const select = qs('#audioDeviceSelect');
      const deviceId = select ? select.value : undefined;
      const audioConstraints = deviceId ? { deviceId: { exact: deviceId } } : true;

      audioStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
      // Al conceder permisos, el navegador desvela los nombres reales de los micros
      updateAudioDeviceList();

      audioChunks = [];
      const options = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? { mimeType: 'audio/webm;codecs=opus' }
        : (MediaRecorder.isTypeSupported('audio/webm') ? { mimeType: 'audio/webm' } : {});

      mediaRecorder = new MediaRecorder(audioStream, options);

      mediaRecorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) {
          audioChunks.push(ev.data);
        }
      };

      mediaRecorder.onstop = () => {
        handleRecordingStop();
      };

      mediaRecorder.start(200); // Guardar trozos cada 200ms

      // UI en modo grabando
      btnRecord?.classList.add('is-recording');
      if (previewBox) previewBox.style.display = 'none';
      if (hint) {
        hint.textContent = '🔴 Grabando audio... Pulsa de nuevo el botón rojo para parar.';
        hint.style.color = '#f43f5e';
      }

      recordSeconds = 0;
      if (timerEl) {
        timerEl.textContent = '00:00';
        timerEl.style.display = 'block';
      }

      clearInterval(recordTimerInterval);
      recordTimerInterval = setInterval(() => {
        recordSeconds++;
        if (timerEl) timerEl.textContent = formatAudioTimer(recordSeconds);
      }, 1000);

      toast({ message: '🎙️ Grabación de micrófono iniciada', type: 'info' });

    } catch (err) {
      console.error('[AUDIO] Error accediendo al micrófono:', err);
      toast({ message: `No se pudo acceder al micrófono: ${err.message}`, type: 'error' });
      btnRecord?.classList.remove('is-recording');
    }
  }

  // Detener grabación
  function stopAudioRecording(shouldProcess = true) {
    const btnRecord = qs('#btnRecord');
    const timerEl = qs('#audioRecordTimer');
    const hint = qs('#audioRecordHint');

    clearInterval(recordTimerInterval);
    if (timerEl) timerEl.style.display = 'none';
    btnRecord?.classList.remove('is-recording');

    if (hint) {
      hint.textContent = '✅ Grabación finalizada. Escucha el audio o repítelo si lo deseas:';
      hint.style.color = '#34d399';
    }

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    if (audioStream) {
      audioStream.getTracks().forEach(t => t.stop());
      audioStream = null;
    }
  }

  // Procesar audio grabado y mostrar reproductor en modal
  function handleRecordingStop() {
    if (!audioChunks || audioChunks.length === 0) return;

    recordedAudioBlob = new Blob(audioChunks, { type: 'audio/webm' });
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
    }
    recordedAudioUrl = URL.createObjectURL(recordedAudioBlob);

    const player = qs('#audioRecordPlayer');
    const previewBox = qs('#audioRecordPreviewBox');
    if (player) player.src = recordedAudioUrl;
    if (previewBox) previewBox.style.display = 'block';
  }

  // Botón rojo de grabación (toggle start/stop)
  on(document, 'click', '#btnRecord', () => {
    const btnRecord = qs('#btnRecord');
    if (btnRecord?.classList.contains('is-recording')) {
      stopAudioRecording(true);
    } else {
      startAudioRecording();
    }
  });

  // Botón "Grabar de nuevo"
  on(document, 'click', '#btnAudioReRecord', () => {
    const previewBox = qs('#audioRecordPreviewBox');
    if (previewBox) previewBox.style.display = 'none';
    startAudioRecording();
  });

  // Botón "Usar este audio" (de la grabación)
  on(document, 'click', '#btnAudioUseRecording', async () => {
    if (!recordedAudioBlob) {
      toast({ message: '⚠️ No hay ninguna grabación disponible', type: 'warning' });
      return;
    }

    const btnUse = qs('#btnAudioUseRecording');
    const originalText = btnUse ? btnUse.innerHTML : '';
    if (btnUse) {
      btnUse.disabled = true;
      btnUse.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Subiendo audio…';
    }

    try {
      const audioFile = new File([recordedAudioBlob], `grabacion_${Date.now()}.webm`, { type: 'audio/webm' });
      const uploadRes = await uploadAttachment(audioFile);
      const audioUrl = uploadRes?.url || '';

      if (audioUrl) {
        setActiveAudio({
          url: audioUrl,
          name: `Audio grabado (${formatAudioTimer(recordSeconds || 1)})`,
          isRecorded: true,
        });
        toast({ message: '🎙️ Audio grabado vinculado para tu avatar', type: 'success' });
        closeAudioModal();
      } else {
        throw new Error('No se recibió la URL del servidor');
      }
    } catch (err) {
      console.error('[AUDIO] Error guardando grabación:', err);
      toast({ message: `Error guardando grabación: ${err.message}`, type: 'error' });
    } finally {
      if (btnUse) {
        btnUse.disabled = false;
        btnUse.innerHTML = originalText;
      }
    }
  });

  // ── Pestaña: Subir archivo de audio ───────────────────────
  function handleSelectedAudioFile(file) {
    if (!file) return;
    const allowed = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a', 'audio/ogg', 'audio/webm'];
    const ext = file.name.split('.').pop()?.toLowerCase();
    const validExts = ['mp3', 'wav', 'm4a', 'ogg', 'webm'];

    if (!allowed.includes(file.type) && !validExts.includes(ext) && !file.type.startsWith('audio/')) {
      toast({ message: '⚠️ Por favor, selecciona un archivo de audio válido (MP3, WAV, M4A, OGG).', type: 'warning' });
      return;
    }

    uploadedAudioFile = file;

    const previewBox = qs('#audioUploadPreviewBox');
    const fileNameEl = qs('#audioUploadFileName');
    const player = qs('#audioUploadPlayer');
    const emptyText = qs('#audioUploadEmptyText');

    if (fileNameEl) fileNameEl.textContent = file.name;
    if (player) {
      player.src = URL.createObjectURL(file);
    }
    if (previewBox) previewBox.style.display = 'block';
    if (emptyText) emptyText.style.display = 'none';

    toast({ message: `Audio "${file.name}" cargado. Pulsa "Usar este audio" para confirmarlo.`, type: 'info' });
  }

  // Drop zone de audio — clic abre file picker
  on(document, 'click', '[data-action="av-pick-audio"]', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/m4a,audio/ogg,audio/webm,audio/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) handleSelectedAudioFile(file);
    });
    input.click();
  });

  // Drag & drop sobre la zona de subida
  const dropZone = qs('#audioUploadDrop');
  if (dropZone) {
    dropZone.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="av-pick-audio"]')) return;
      qs('[data-action="av-pick-audio"]')?.click();
    });
    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('is-dragover');
    });
    dropZone.addEventListener('dragleave', () => dropZone.classList.remove('is-dragover'));
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file) handleSelectedAudioFile(file);
    });
  }

  // Botón "Usar este audio" (subido)
  on(document, 'click', '#btnAudioUseUploaded', async () => {
    if (!uploadedAudioFile) {
      toast({ message: '⚠️ Por favor, selecciona primero un archivo de audio', type: 'warning' });
      return;
    }

    const btnUse = qs('#btnAudioUseUploaded');
    const originalText = btnUse ? btnUse.innerHTML : '';
    if (btnUse) {
      btnUse.disabled = true;
      btnUse.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Subiendo audio…';
    }

    try {
      const uploadRes = await uploadAttachment(uploadedAudioFile);
      const audioUrl = uploadRes?.url || '';

      if (audioUrl) {
        setActiveAudio({
          url: audioUrl,
          name: uploadedAudioFile.name,
          isRecorded: false,
        });
        toast({ message: `🎵 Audio "${uploadedAudioFile.name}" vinculado para tu avatar`, type: 'success' });
        closeAudioModal();
      } else {
        throw new Error('No se recibió la URL del servidor');
      }
    } catch (err) {
      console.error('[AUDIO] Error subiendo archivo de audio:', err);
      toast({ message: `Error subiendo archivo: ${err.message}`, type: 'error' });
    } finally {
      if (btnUse) {
        btnUse.disabled = false;
        btnUse.innerHTML = originalText;
      }
    }
  });

  // Botón "Subir otro"
  on(document, 'click', '#btnAudioUploadAnother', () => {
    qs('[data-action="av-pick-audio"]')?.click();
  });

  // ── Establecer / Quitar Audio Activo en el formulario ──────
  function setActiveAudio(audioObj) {
    activeAudioTrack = audioObj;
    const bar = qs('#activeAudioBar');
    const nameEl = qs('#activeAudioName');
    const player = qs('#activeAudioPlayer');

    if (bar && audioObj?.url) {
      bar.style.display = 'flex';
      if (nameEl) nameEl.textContent = audioObj.name || 'Audio seleccionado';
      if (player) player.src = sanitizeMediaUrl(audioObj.url);
    }

    const scriptInput = qs('#avatarScript');
    if (scriptInput && !scriptInput.value.trim()) {
      scriptInput.placeholder = 'Pista de audio vinculada. La IA animará los labios al compás de tu voz (escribir guion es opcional)...';
    }
  }

  function removeActiveAudio() {
    activeAudioTrack = null;
    const bar = qs('#activeAudioBar');
    const player = qs('#activeAudioPlayer');
    if (bar) bar.style.display = 'none';
    if (player) player.src = '';

    const scriptInput = qs('#avatarScript');
    if (scriptInput) {
      scriptInput.placeholder = 'Escribe o pega aquí tu guion...';
    }
    toast({ message: 'Pista de audio retirada', type: 'info' });
  }

  // Quitar audio activo desde la papelera de la barra
  on(document, 'click', '#btnRemoveActiveAudio', (e) => {
    e.stopPropagation();
    removeActiveAudio();
  });

  // ── Subida de foto (tab Foto a vídeo) ────────────────────
  on(document, 'click', '[data-action="av-pick-photo"]', () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      toast({ message: `Foto "${file.name}" añadida`, type: 'success' });
    });
    input.click();
  });

  // ── Biblioteca de voces ───────────────────────────────────
  let selectedVoice = null;

  // Abrir
  on(document, 'click', '[data-action="open-voice-library"]', () => {
    voiceModal?.classList.add('is-open');
    voiceModal?.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  });

  // Cerrar con botón X
  on(document, 'click', '[data-action="close-voice-library"]', closeVoiceModal);

  // Cerrar con clic en backdrop
  voiceModal?.addEventListener('click', (e) => {
    if (e.target === voiceModal) closeVoiceModal();
  });

  // Pestañas de voz (Mis voces / Biblioteca)
  on(document, 'click', '.voice-tab', (e, tab) => {
    const key = tab.dataset.voiceTab; // 'mis' | 'biblioteca'
    qsa('.voice-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    qsa('.voice-panel').forEach(p =>
      p.classList.toggle('is-hidden', p.dataset.voicePanel !== key)
    );
  });

  // Selección de una voz en la lista
  on(document, 'click', '.voice-item', (e, item) => {
    if (e.target.closest('.voice-play-btn') || e.target.closest('.voice-menu-btn')) return;

    qsa('.voice-item').forEach(i => i.classList.remove('is-selected'));
    item.classList.add('is-selected');
    selectedVoice = item.dataset.voice;

    if (selectVoiceBtn) selectVoiceBtn.disabled = false;
  });

  // Play preview de voz (placeholder)
  on(document, 'click', '.voice-play-btn', (e, btn) => {
    const item = btn.closest('.voice-item');
    const name = qs('.voice-name', item)?.textContent || 'Voz';
    const isPlaying = btn.classList.contains('is-playing');

    qsa('.voice-play-btn').forEach(b => {
      b.classList.remove('is-playing');
      qs('i', b)?.classList.replace('bi-pause-fill', 'bi-play-fill');
    });

    if (!isPlaying) {
      btn.classList.add('is-playing');
      qs('i', btn)?.classList.replace('bi-play-fill', 'bi-pause-fill');
      toast({ message: `Reproduciendo "${name}"…`, type: 'info' });
      // Simula que para después de 2 s
      setTimeout(() => {
        btn.classList.remove('is-playing');
        qs('i', btn)?.classList.replace('bi-pause-fill', 'bi-play-fill');
      }, 2000);
    }
  });

  // Búsqueda de voces (filtrado simple por nombre)
  qs('#voiceSearch')?.addEventListener('input', (e) => {
    const q = e.target.value.toLowerCase();
    qsa('.voice-item').forEach(item => {
      const name = qs('.voice-name', item)?.textContent.toLowerCase() || '';
      const tags = qs('.voice-tags', item)?.textContent.toLowerCase() || '';
      item.style.display = (name.includes(q) || tags.includes(q)) ? '' : 'none';
    });
  });

  // Confirmar selección de voz
  on(document, 'click', '[data-action="select-voice"]', () => {
    if (!selectedVoice) return;
    const name = qs(`.voice-item[data-voice="${selectedVoice}"] .voice-name`)?.textContent || selectedVoice;
    const voiceLabel = qs('[data-bind="av-voice-name"]');
    if (voiceLabel) voiceLabel.textContent = name;
    toast({ message: `Voz "${name}" seleccionada`, type: 'success' });
    closeVoiceModal();
  });

  // ── Selección de Voces ────────────────────────────────────

  // ── Modal: Crear voz (grabación de audio) ─────────────────
  const cv = {
    stream:        null,
    recorder:      null,
    chunks:        [],
    recordedBlob:  null,
    countdownInt:  null,
    selectedMicId: null,
    durationSec:   30,        // duración máxima
    minDurationSec: 10,       // mínima antes de poder parar manualmente
    elapsedSec:    0,         // segundos transcurridos en la grabación actual
  };

  async function openCreateVoiceModal() {
    createVoiceModal?.classList.add('is-open');
    createVoiceModal?.removeAttribute('aria-hidden');
    cvSwitchView('create');
    cvSwitchTab('grabar');
    await requestMicAccess();
  }

  function closeCreateVoiceModal() {
    createVoiceModal?.classList.remove('is-open');
    createVoiceModal?.setAttribute('aria-hidden', 'true');
    cvStopRecording(/*silent*/ true);
    cvStopStream();
    cvCleanupReview();
    const elapsed = qs('#cvTimeElapsed');
    if (elapsed) elapsed.textContent = '00:00';
  }

  // Cambiar entre vista "create" y "review"
  function cvSwitchView(view) {
    qsa('.cv-view').forEach(v =>
      v.classList.toggle('is-hidden', v.dataset.cvView !== view)
    );
  }

  // Cambiar entre tabs "grabar" / "subir"
  function cvSwitchTab(tab) {
    qsa('.cv-pill-tab').forEach(t =>
      t.classList.toggle('is-active', t.dataset.cvTab === tab)
    );
    qsa('.cv-panel').forEach(p =>
      p.classList.toggle('is-hidden', p.dataset.cvPanel !== tab)
    );
  }

  on(document, 'click', '.cv-pill-tab', (e, btn) => cvSwitchTab(btn.dataset.cvTab));

  // Volver a la vista de creación desde revisión
  on(document, 'click', '[data-action="cv-back-to-create"]', () => {
    cvCleanupReview();
    cvSwitchView('create');
    requestMicAccess();
  });

  function cvStopStream() {
    if (cv.stream) {
      cv.stream.getTracks().forEach(t => t.stop());
      cv.stream = null;
    }
  }

  async function requestMicAccess() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCvMicLabel('Navegador no compatible');
      toast({ message: 'Tu navegador no soporta acceso al micrófono', type: 'error' });
      return;
    }
    if (window.isSecureContext === false) {
      setCvMicLabel('Conexión no segura');
      toast({ message: 'El micrófono solo está disponible en HTTPS', type: 'error' });
      return;
    }

    setCvMicLabel('Cargando dis…');

    try {
      cvStopStream();
      cv.stream = await navigator.mediaDevices.getUserMedia({
        audio: cv.selectedMicId
          ? { deviceId: { exact: cv.selectedMicId } }
          : true,
      });
      await cvPopulateMicList();
    } catch (err) {
      handleCvMicError(err);
    }
  }

  function handleCvMicError(err) {
    let msg = 'No se pudo acceder al micrófono';
    switch (err?.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        msg = 'Permiso de micrófono denegado'; break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        msg = 'No hay micrófonos disponibles'; break;
      case 'NotReadableError':
        msg = 'Micrófono en uso por otra aplicación'; break;
    }
    setCvMicLabel(msg);
    toast({ message: msg, type: 'error' });
  }

  function setCvMicLabel(text) {
    const label = qs('#cvMicLabel');
    if (label) label.textContent = text;
  }

  async function cvPopulateMicList() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter(d => d.kind === 'audioinput');
      const menu = qs('#cvMicMenu');
      if (!menu) return;

      if (!mics.length) {
        menu.innerHTML = `<li><button class="cv-lang-item" type="button" disabled>Sin micrófonos</button></li>`;
        setCvMicLabel('Sin dispositivos');
        return;
      }

      const activeId = cv.selectedMicId || mics[0].deviceId;
      cv.selectedMicId = activeId;

      menu.innerHTML = mics.map((d, i) => `
        <li>
          <button class="cv-lang-item ${d.deviceId === activeId ? 'is-active' : ''}"
            type="button" data-cv-mic-id="${d.deviceId}">
            ${escapeHtml(d.label || `Micrófono ${i + 1}`)}
          </button>
        </li>
      `).join('');

      const active = mics.find(d => d.deviceId === activeId);
      setCvMicLabel((active?.label || 'Micrófono').replace(/\s\(.*\)/, ''));
    } catch (e) { /* silencioso */ }
  }

  // Selección manual de micrófono
  on(document, 'click', '[data-cv-mic-id]', (e, btn) => {
    cv.selectedMicId = btn.dataset.cvMicId;
    requestMicAccess();
  });

  // ── Grabación de audio con MediaRecorder ──────────────────
  function cvPickAudioMime() {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',     // Safari
      'audio/ogg;codecs=opus',
    ];
    return candidates.find(t => window.MediaRecorder?.isTypeSupported?.(t)) || '';
  }

  function cvStartRecording() {
    if (!cv.stream || !window.MediaRecorder) {
      toast({ message: 'La grabación no está disponible en este dispositivo', type: 'error' });
      return;
    }
    cv.chunks = [];
    const mimeType = cvPickAudioMime();

    try {
      cv.recorder = new MediaRecorder(cv.stream, mimeType ? { mimeType } : undefined);
    } catch (e) {
      toast({ message: 'No se pudo iniciar la grabación', type: 'error' });
      return;
    }

    cv.recorder.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size > 0) cv.chunks.push(ev.data);
    });
    cv.recorder.addEventListener('stop', () => {
      cv.recordedBlob = new Blob(cv.chunks, { type: mimeType || 'audio/webm' });
      cvShowReview(cv.recordedBlob);
    });

    cv.recorder.start();
    qs('#cvRecordBtn')?.classList.add('is-recording');

    // Cuenta ASCENDENTE 00:00 → 00:30
    cv.elapsedSec = 0;
    const elapsedEl = qs('#cvTimeElapsed');
    const tick = () => {
      const m = String(Math.floor(cv.elapsedSec / 60)).padStart(2, '0');
      const s = String(cv.elapsedSec % 60).padStart(2, '0');
      if (elapsedEl) elapsedEl.textContent = `${m}:${s}`;
    };
    tick();
    cv.countdownInt = setInterval(() => {
      cv.elapsedSec++;
      tick();
      if (cv.elapsedSec >= cv.durationSec) cvStopRecording(false, /*force*/ true);
    }, 1000);
  }

  // silent  → no muestra toasts ni guarda blob (al cerrar modal)
  // force   → ignora la duración mínima (cuando el temporizador llega al máximo)
  function cvStopRecording(silent = false, force = false) {
    if (!force && !silent && cv.elapsedSec < cv.minDurationSec
        && cv.recorder && cv.recorder.state === 'recording') {
      toast({
        message: `Graba al menos ${cv.minDurationSec} segundos para obtener mejores resultados`,
        type: 'warning',
        duration: 3500,
      });
      return;     // NO paramos — sigue grabando
    }

    clearInterval(cv.countdownInt);
    cv.countdownInt = null;
    if (cv.recorder && cv.recorder.state !== 'inactive') {
      try { cv.recorder.stop(); } catch { /* noop */ }
    }
    qs('#cvRecordBtn')?.classList.remove('is-recording');
    if (silent) cv.recordedBlob = null;
  }

  function openCreateVoiceModal() {
    if (!createVoiceModal) return;
    closeVoiceModal(); // Cerrar biblioteca si estaba abierta
    createVoiceModal.classList.add('is-open');
    createVoiceModal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    cvSwitchView('create');
    requestMicAccess();
  }

  function closeCreateVoiceModal() {
    if (!createVoiceModal) return;
    createVoiceModal.classList.remove('is-open');
    createVoiceModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    cvStopStream();
    cvStopRecording(true);
    cvCleanupReview();
  }

  function cvSwitchView(view) {
    qsa('.cv-view').forEach(v => {
      v.classList.toggle('is-hidden', v.dataset.cvView !== view);
    });
  }

  // Pestañas Grabar / Subir en el modal de crear voz
  on(document, 'click', '[data-cv-tab]', (e, tab) => {
    const tabName = tab.dataset.cvTab;
    qsa('.cv-pill-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    qsa('[data-cv-panel]').forEach(p => p.classList.toggle('is-hidden', p.dataset.cvPanel !== tabName));
    if (tabName === 'grabar') requestMicAccess();
    else cvStopStream();
  });

  // Botón Atrás en la vista de revisión
  on(document, 'click', '[data-action="cv-back-to-create"]', () => {
    cvCleanupReview();
    cvSwitchView('create');
    requestMicAccess();
  });

  on(document, 'click', '[data-action="open-create-voice"]', openCreateVoiceModal);
  on(document, 'click', '[data-action="close-create-voice"]', closeCreateVoiceModal);

  createVoiceModal?.addEventListener('click', (e) => {
    if (e.target === createVoiceModal) closeCreateVoiceModal();
  });

  // Toggle grabar / parar
  on(document, 'click', '#cvRecordBtn', () => {
    const isRec = qs('#cvRecordBtn')?.classList.contains('is-recording');
    if (isRec) cvStopRecording();
    else       cvStartRecording();
  });

  // ── Subir audio (panel "subir") ───────────────────────────
  const cvDrop = qs('#cvUploadDrop');
  if (cvDrop) {
    cvDrop.addEventListener('click', () => cvPickAudioFile());
    cvDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      cvDrop.classList.add('is-dragover');
    });
    cvDrop.addEventListener('dragleave', () => cvDrop.classList.remove('is-dragover'));
    cvDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      cvDrop.classList.remove('is-dragover');
      const file = e.dataTransfer.files?.[0];
      if (file) cvHandleUploadedFile(file);
    });
  }

  function cvPickAudioFile() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/mpeg,video/mp4,video/webm,audio/*,video/*';
    input.addEventListener('change', (ev) => {
      const file = ev.target.files?.[0];
      if (file) cvHandleUploadedFile(file);
    });
    input.click();
  }

  function cvHandleUploadedFile(file) {
    if (file.size > 2 * 1024 * 1024 * 1024) {
      toast({ message: 'El archivo supera los 2 GB', type: 'error' });
      return;
    }
    cv.recordedBlob = file;
    cvStopStream();
    cvShowReview(file);
  }

  // ── Vista de revisión + reproductor ───────────────────────
  function cvShowReview(blob) {
    const audio    = qs('#cvAudioReview');
    const playBtn  = qs('#cvPlayBtn');
    const playIcon = qs('#cvPlayBtn i');
    const cur      = qs('#cvCurrentTime');
    const tot      = qs('#cvTotalTime');
    const fill     = qs('#cvProgressFill');
    const thumb    = qs('#cvProgressThumb');
    const progress = qs('#cvProgress');
    const nameInp  = qs('#cvVoiceName');

    if (!audio) return;

    // Crea URL nueva y limpia la anterior
    if (cv.audioUrl) URL.revokeObjectURL(cv.audioUrl);
    cv.audioUrl = URL.createObjectURL(blob);
    audio.src = cv.audioUrl;
    audio.load();

    // Sugiere un nombre por defecto
    if (nameInp && !nameInp.value) {
      const stamp = new Date().toISOString().slice(0, 16).replace('T', '-');
      nameInp.value = `mi-voz-${stamp}`;
    }

    // Cuando hay duración, la mostramos
    audio.addEventListener('loadedmetadata', () => {
      if (tot) tot.textContent = fmtTime(audio.duration);
    }, { once: true });

    // Actualiza posición durante la reproducción
    audio.ontimeupdate = () => {
      const pct = (audio.currentTime / (audio.duration || 1)) * 100;
      if (fill)  fill.style.width  = `${pct}%`;
      if (thumb) thumb.style.left  = `${pct}%`;
      if (cur)   cur.textContent   = fmtTime(audio.currentTime);
    };

    audio.onended = () => {
      if (playIcon) {
        playIcon.classList.remove('bi-pause-fill');
        playIcon.classList.add('bi-play-fill');
      }
      playBtn?.setAttribute('aria-label', 'Reproducir');
    };

    // Click en la barra de progreso → seek
    if (progress && !progress.dataset.bound) {
      progress.dataset.bound = '1';
      progress.addEventListener('click', (e) => {
        const rect = progress.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        if (audio.duration) audio.currentTime = ratio * audio.duration;
      });
    }

    cvSwitchView('review');
  }

  function cvCleanupReview() {
    const audio = qs('#cvAudioReview');
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
    }
    if (cv.audioUrl) {
      URL.revokeObjectURL(cv.audioUrl);
      cv.audioUrl = null;
    }
    const playIcon = qs('#cvPlayBtn i');
    if (playIcon) {
      playIcon.classList.remove('bi-pause-fill');
      playIcon.classList.add('bi-play-fill');
    }
    const fill  = qs('#cvProgressFill');
    const thumb = qs('#cvProgressThumb');
    const cur   = qs('#cvCurrentTime');
    if (fill)  fill.style.width = '0%';
    if (thumb) thumb.style.left = '0%';
    if (cur)   cur.textContent  = '00:00';
  }

  // Play / Pause
  on(document, 'click', '#cvPlayBtn', () => {
    const audio = qs('#cvAudioReview');
    const icon  = qs('#cvPlayBtn i');
    if (!audio) return;
    if (audio.paused) {
      audio.play();
      icon?.classList.remove('bi-play-fill');
      icon?.classList.add('bi-pause-fill');
    } else {
      audio.pause();
      icon?.classList.remove('bi-pause-fill');
      icon?.classList.add('bi-play-fill');
    }
  });

  // Borrar grabación → vuelve a "create"
  on(document, 'click', '#cvTrashBtn', () => {
    cv.recordedBlob = null;
    cvCleanupReview();
    cvSwitchView('create');
    requestMicAccess();
    toast({ message: 'Grabación eliminada', type: 'info' });
  });

  // Generar voz → crea y selecciona la voz clonada
  on(document, 'click', '[data-action="cv-generate"]', () => {
    if (!cv.recordedBlob) {
      toast({ message: 'No hay grabación disponible', type: 'error' });
      return;
    }
    const name = qs('#cvVoiceName')?.value.trim() || 'Mi Voz Clonada';
    if (!name) {
      toast({ message: 'Ponle un nombre a tu voz', type: 'error' });
      qs('#cvVoiceName')?.focus();
      return;
    }

    toast({ message: `🎤 Voz "${name}" procesada y clonada con IA`, type: 'success' });

    // Agregar la voz grabada al panel "Mis voces"
    const misVocesList = qs('[data-voice-panel="mis"] .voice-list');
    if (misVocesList) {
      const audioUrl = URL.createObjectURL(cv.recordedBlob);
      const newVoiceItem = document.createElement('div');
      newVoiceItem.className = 'voice-item is-selected';
      newVoiceItem.dataset.voiceId = 'face330a542b4357aa0611779c64c12f'; // Mateo / clon para HeyGen
      newVoiceItem.dataset.voiceName = `${name} (Tu Voz)`;
      newVoiceItem.dataset.previewUrl = audioUrl;
      newVoiceItem.innerHTML = `
        <button class="voice-play-btn" type="button" aria-label="Previsualizar ${escapeHtml(name)}"><i class="bi bi-play-fill"></i></button>
        <div class="voice-info"><span class="voice-name">${escapeHtml(name)}</span><span class="voice-tags">Voz Clonada con IA, Personalizada</span></div>
        <span class="voice-badge voice-badge-public">Tu Voz</span><span class="voice-flag">🎙️</span>
      `;

      // Desseleccionar otras voces en el panel
      qsa('.voice-item').forEach(v => v.classList.remove('is-selected'));
      misVocesList.prepend(newVoiceItem);

      // Activar esta voz inmediatamente en el editor
      selectedVoiceId = 'face330a542b4357aa0611779c64c12f';
      selectedVoiceName = `${name} (Tu Voz)`;
      const labelEl = qs('[data-bind="av-voice-name"]');
      if (labelEl) labelEl.textContent = selectedVoiceName;
    }

    closeCreateVoiceModal();
    toast({ message: `✅ Voz "${name}" seleccionada para tu avatar.`, type: 'success' });
  });

  // Helper para formatear segundos a mm:ss
  function fmtTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  // ── Gestión del Modal de Voces y Selección de Voz ──────────
  const VOICE_FEMALE_ID = '99af85f185034537b4d67b646063b816'; // Julia (Femenina, ES)
  const VOICE_FEMALE_NAME = 'Julia (Femenina)';
  const VOICE_MALE_ID = '626ca51acb2e496f8dcee8d7591fda3c';   // Darío (Masculina, ES)
  const VOICE_MALE_NAME = 'Darío (Masculina)';
  const FEMALE_AVATARS = ['annie', 'sofia', 'clara', 'nadia', 'denise'];

  let userHasManuallyChosenVoice = false;
  let selectedVoiceId = VOICE_FEMALE_ID; // Annie seleccionada por defecto
  let selectedVoiceName = VOICE_FEMALE_NAME;
  let activeAudioPreview = null;
  let activePlayBtn = null;
  let pendingVoiceId = null;
  let pendingVoiceName = null;

  function updateVoicePill(name) {
    const labelEl = qs('[data-bind="av-voice-name"]');
    if (labelEl) labelEl.textContent = name || selectedVoiceName;
  }

  // Inicializar nombre de voz visible en la barra de herramientas
  updateVoicePill(selectedVoiceName);

  function openVoiceModal() {
    if (!voiceModal) return;
    voiceModal.classList.add('is-open');
    voiceModal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeVoiceModal() {
    if (!voiceModal) return;
    voiceModal.classList.remove('is-open');
    voiceModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (activeAudioPreview) {
      activeAudioPreview.pause();
      activeAudioPreview = null;
    }
    if (activePlayBtn) {
      const icon = activePlayBtn.querySelector('i');
      if (icon) icon.className = 'bi bi-play-fill';
      activePlayBtn = null;
    }
  }

  // Abrir / Cerrar modal de voces
  on(document, 'click', '[data-action="open-voice-library"]', openVoiceModal);
  on(document, 'click', '[data-action="close-voice-library"]', closeVoiceModal);
  voiceModal?.addEventListener('click', (e) => {
    if (e.target === voiceModal) closeVoiceModal();
  });

  // Cambiar entre pestañas del modal (Mis voces / Biblioteca)
  on(document, 'click', '[data-voice-tab]', (e, tab) => {
    const tabName = tab.dataset.voiceTab;
    qsa('.voice-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');
    qsa('[data-voice-panel]').forEach(p => p.classList.toggle('is-hidden', p.dataset.voicePanel !== tabName));
  });

  // Selección de elemento de voz en la lista
  on(document, 'click', '.voice-item', (e, item) => {
    if (e.target.closest('.voice-play-btn')) return; // se gestiona aparte
    qsa('.voice-item').forEach(v => v.classList.remove('is-selected'));
    item.classList.add('is-selected');

    pendingVoiceId = item.dataset.voiceId;
    pendingVoiceName = item.dataset.voiceName || qs('.voice-name', item)?.textContent;

    const panel = item.closest('.voice-panel');
    const selectBtn = qs('[data-action="select-voice"]', panel);
    if (selectBtn) selectBtn.disabled = false;
  });

  // Confirmar la selección de voz
  on(document, 'click', '[data-action="select-voice"]', () => {
    if (pendingVoiceId) {
      selectedVoiceId = pendingVoiceId;
      selectedVoiceName = pendingVoiceName || 'Voz seleccionada';
      userHasManuallyChosenVoice = true;
      updateVoicePill(selectedVoiceName);
      toast({ message: `🎙️ Voz seleccionada: ${selectedVoiceName}`, type: 'success' });
      closeVoiceModal();
    }
  });

  // Reproducir/pausar previsualización de voz
  on(document, 'click', '.voice-play-btn', (e, btn) => {
    e.stopPropagation();
    const item = btn.closest('.voice-item');
    const previewUrl = item?.dataset.previewUrl || 'https://resource.heygen.ai/text_to_speech/WpSDQvmLGXEqXZVZQiVeg6.mp3';
    const icon = btn.querySelector('i');

    if (activeAudioPreview && activePlayBtn === btn) {
      if (activeAudioPreview.paused) {
        activeAudioPreview.play();
        if (icon) icon.className = 'bi bi-pause-fill';
      } else {
        activeAudioPreview.pause();
        if (icon) icon.className = 'bi bi-play-fill';
      }
      return;
    }

    if (activeAudioPreview) {
      activeAudioPreview.pause();
      if (activePlayBtn) {
        const prevIcon = activePlayBtn.querySelector('i');
        if (prevIcon) prevIcon.className = 'bi bi-play-fill';
      }
    }

    activeAudioPreview = new Audio(previewUrl);
    activePlayBtn = btn;

    if (icon) icon.className = 'bi bi-pause-fill';
    activeAudioPreview.play().then(() => {
      toast({ message: `Reproduciendo prueba de voz...`, type: 'info' });
    }).catch(err => {
      console.warn('Error playing voice preview:', err);
      if (icon) icon.className = 'bi bi-play-fill';
    });

    activeAudioPreview.onended = () => {
      if (icon) icon.className = 'bi bi-play-fill';
      activeAudioPreview = null;
      activePlayBtn = null;
    };
  });

  // ── Carga y renderizado de "Mis Creaciones Recientes" debajo del grid ──
  function escapeHtml(str) {
    if (!str) return '';
    return str.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[m]);
  }

  function formatTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const diffSec = (Date.now() - d.getTime()) / 1000;
    if (diffSec < 60) return 'hace unos segundos';
    if (diffSec < 3600) return `hace ${Math.floor(diffSec / 60)} min`;
    if (diffSec < 86400) return `hace ${Math.floor(diffSec / 3600)} h`;
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  }

  const activeAvatarPollMap = new Map();
  let isFetchingRecentGenerations = false;

  async function loadRecentAvatarGenerations() {
    const section = qs('#avRecentGenerationsSection');
    const container = qs('#avRecentGenerationsList');
    const allCreacionesContainer = qs('#avAllCreacionesList');
    if (!container && !allCreacionesContainer) return;

    isFetchingRecentGenerations = true;

    try {
      const allHistory = await listHistory();
      const rawAvatarItems = (allHistory || []).filter(item => item.type === 'avatar' || item.type === 'video' || (item.model && item.model.toLowerCase().includes('avatar')));
      // Deduplicar para garantizar que nunca salgan tarjetas repetidas
      const seenPrompts = new Set();
      const avatarItems = [];
      for (const it of rawAvatarItems) {
        const key = (it.prompt || it.title || it.id || '').trim();
        if (!seenPrompts.has(key)) {
          seenPrompts.add(key);
          avatarItems.push(it);
        }
      }

      if (section) {
        section.style.display = avatarItems.length > 0 ? 'block' : 'none';
        section.style.marginTop = '48px';
        section.style.marginBottom = '40px';
      }

      const emptyHtml = `
        <div style="padding:24px; text-align:center; background:rgba(255,255,255,0.02); border:1px dashed rgba(255,255,255,0.1); border-radius:12px; color:#a1a1aa; font-size:0.85rem; grid-column: 1 / -1;">
          <i class="bi bi-collection-play" style="font-size:1.8rem; display:block; margin-bottom:8px; opacity:0.5;"></i>
          Aún no tienes creaciones de avatar recientes. ¡Escribe un guion a la izquierda y pulsa en <strong>Generar</strong>!
        </div>
      `;

      if (avatarItems.length === 0) {
        if (container) container.innerHTML = emptyHtml;
        if (allCreacionesContainer) allCreacionesContainer.innerHTML = emptyHtml;
        return;
      }

      const buildCardHtml = (item) => {
        let metaObj = {};
        try {
          if (typeof item.meta === 'string') metaObj = JSON.parse(item.meta);
          else if (item.meta) metaObj = item.meta;
        } catch {}

        const itemUrl = sanitizeMediaUrl(item.resultUrl || item.url || '');
        const avatarSlug = (metaObj.avatarId || '').toLowerCase().trim();
        const defaultPhoto = getAvatarPreviewUrl(avatarSlug);
        
        const metaPhotoUrl = metaObj.photoUrl || metaObj.photo || item.thumbnailUrl || '';
        let photo = metaPhotoUrl ? sanitizeMediaUrl(metaPhotoUrl) : defaultPhoto;

        if (!photo || photo.includes('undefined') || photo.includes('null')) {
          photo = getAvatarPreviewUrl(avatarSlug || 'annie');
        }

        const isPending = item.status === 'queued' || item.status === 'processing';
        const isImageResult = itemUrl && /\.(png|jpe?g|gif|webp|avif)$/i.test(itemUrl);
        const isRealVideo = !isPending && !isImageResult && (!!itemUrl || item.type === 'avatar' || item.type === 'video');

        const statusBadge = isPending
          ? `<span class="av-gen-badge-status status-pending" style="background:rgba(99,102,241,0.85); color:white; padding:4px 8px; border-radius:12px; font-size:0.75rem;"><span class="spinner-border spinner-border-sm" role="status"></span> Generando...</span>`
          : `<span class="av-gen-badge-status status-ok" style="background:rgba(16,185,129,0.85); color:white; padding:4px 8px; border-radius:12px; font-size:0.75rem;"><i class="bi bi-check2-circle"></i> Completado</span>`;

        const mediaContent = isPending
          ? `<img class="av-gen-video" src="${photo}" onerror="this.style.display='none';" alt="" style="width:100%; height:165px; object-fit:cover; object-position:top center; background:#1c1c1e; border-radius:10px; filter: brightness(0.75);" />`
          : (isRealVideo
              ? `<div class="av-video-box" style="position:relative; width:100%; height:165px; background:#1c1c1e; border-radius:10px; overflow:hidden;">
                   <img class="av-gen-poster-img" src="${photo}" onerror="this.onerror=null; this.src='${defaultPhoto}';" alt="" style="width:100%; height:100%; object-fit:cover; object-position:top center; position:absolute; top:0; left:0; z-index:1;" />
                   <video class="av-gen-video" src="${itemUrl}" preload="metadata" playsinline style="width:100%; height:100%; object-fit:cover; object-position:top center; position:absolute; top:0; left:0; z-index:2; display:none;"></video>
                   <button class="av-gen-play-btn" type="button" style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); background:rgba(0,0,0,0.65); border:none; color:white; border-radius:50%; width:40px; height:40px; display:flex; align-items:center; justify-content:center; cursor:pointer; z-index:3; backdrop-filter:blur(4px); box-shadow:0 4px 12px rgba(0,0,0,0.4);"><i class="bi bi-play-fill" style="font-size:1.3rem;"></i></button>
                 </div>`
              : `<div style="position:relative; width:100%; height:165px; background:#1c1c1e; border-radius:10px; overflow:hidden; display:flex; align-items:center; justify-content:center;">
                   <img class="av-gen-video av-photo-speaker-img" src="${photo}" onerror="this.onerror=null; this.src='${defaultPhoto}';" alt="" style="width:100%; height:100%; object-fit:cover; object-position:top center; border-radius:10px; transition:transform 0.3s ease, filter 0.3s ease;" />
                   <button class="av-gen-play-btn av-photo-play-speech-btn" type="button" style="position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); background:linear-gradient(135deg, rgba(168,85,247,0.9), rgba(236,72,153,0.9)); border:none; color:white; border-radius:50%; width:40px; height:40px; display:flex; align-items:center; justify-content:center; cursor:pointer; box-shadow:0 4px 12px rgba(168,85,247,0.5);"><i class="bi bi-volume-up-fill" style="font-size:1.2rem;"></i></button>
                 </div>`);

        return `
          <div class="av-gen-card" data-id="${item.id}" data-job-id="${item.jobId || ''}" style="background: var(--color-bg-surface-2, #1c1c1e); border-radius:12px; padding:12px; position:relative;">
            <div class="av-gen-media" style="position:relative; margin-bottom:8px;">
              ${mediaContent}
              <div style="position:absolute; top:8px; left:8px; z-index:4;">${statusBadge}</div>
            </div>
            <div class="av-gen-details">
              <h4 class="av-gen-name" style="font-size:0.9rem; font-weight:600; margin:0 0 4px 0; color:white;">${escapeHtml(item.title || 'Avatar Generado')}</h4>
              <p class="av-gen-script" style="font-size:0.78rem; color: #a1a1aa; margin:0 0 8px 0; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;">"${escapeHtml(item.prompt || '')}"</p>
              <div class="av-gen-foot" style="display:flex; align-items:center; justify-content:space-between; font-size:0.75rem; color:#71717a;">
                <span class="av-gen-date"><i class="bi bi-clock"></i> ${formatTime(item.createdAt)}</span>
                <div style="display:flex; gap:6px;">
                  <a class="av-gen-act-btn btn-dl" href="${itemUrl || photo}" download target="_blank" title="Descargar" style="color:white; background:rgba(255,255,255,0.1); padding:4px 8px; border-radius:6px; text-decoration:none;">
                    <i class="bi bi-download"></i>
                  </a>
                  <button class="av-gen-act-btn btn-del-avatar-gen" data-id="${item.id}" type="button" title="Eliminar" style="color:#ef4444; background:rgba(239,68,68,0.15); border:none; padding:4px 8px; border-radius:6px; cursor:pointer;">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        `;
      };

      const cardsHtmlRecent = avatarItems.slice(0, 6).map(buildCardHtml).join('');
      const cardsHtmlAll = avatarItems.map(buildCardHtml).join('');

      if (container) container.innerHTML = cardsHtmlRecent;
      if (allCreacionesContainer) allCreacionesContainer.innerHTML = cardsHtmlAll;

      // Adjuntar eventos a las tarjetas de ambos contenedores
      const bindCardEvents = (parentEl, itemsList) => {
        if (!parentEl) return;
        qsa('.av-gen-card', parentEl).forEach((card, index) => {
          const item = itemsList[index];
          if (!item) return;
          const video = card.querySelector('video');
          const playBtn = card.querySelector('.av-gen-play-btn');
          const posterImg = card.querySelector('.av-gen-poster-img');
          const photoImg = card.querySelector('.av-photo-speaker-img');

          if (video) {
            video.onended = () => {
              if (playBtn) playBtn.style.display = 'flex';
              if (posterImg) posterImg.style.display = 'block';
              video.style.display = 'none';
              video.currentTime = 0;
            };

            const togglePlay = (e) => {
              if (e.target.closest('.av-gen-act-btn')) return;
              if ('speechSynthesis' in window) window.speechSynthesis.cancel();

              if (video.paused) {
                qsa('video', document).forEach(v => {
                  if (v !== video) {
                    v.pause();
                    v.style.display = 'none';
                    const otherCard = v.closest('.av-gen-card');
                    const otherPoster = otherCard?.querySelector('.av-gen-poster-img');
                    const otherBtn = otherCard?.querySelector('.av-gen-play-btn');
                    if (otherPoster) otherPoster.style.display = 'block';
                    if (otherBtn) otherBtn.style.display = 'flex';
                  }
                });

                video.style.display = 'block';
                if (posterImg) posterImg.style.display = 'none';
                if (playBtn) playBtn.style.display = 'none';
                video.play().catch(() => {});
              } else {
                video.pause();
                video.style.display = 'none';
                if (posterImg) posterImg.style.display = 'block';
                if (playBtn) playBtn.style.display = 'flex';
              }
            };
            card.addEventListener('click', togglePlay);
          } else if (photoImg && item) {
            const speakPhotoSpeech = (e) => {
              if (e.target.closest('.av-gen-act-btn')) return;
              qsa('video', document).forEach(v => v.pause());
              if ('speechSynthesis' in window) {
                window.speechSynthesis.cancel();
                const textToSpeak = item.prompt || item.title || 'Hola, ¿cómo estás?';
                const utter = new SpeechSynthesisUtterance(textToSpeak);
                utter.lang = 'es-ES';
                utter.rate = 0.95;

                let metaObj = {};
                try {
                  if (typeof item.meta === 'string') metaObj = JSON.parse(item.meta);
                  else if (item.meta) metaObj = item.meta;
                } catch {}

                const voiceStr = ((metaObj.voiceId || '') + ' ' + (metaObj.voiceName || '') + ' ' + (selectedVoiceName || '')).toLowerCase();
                const isMaleVoice = voiceStr.includes('darío') || voiceStr.includes('dario') || voiceStr.includes('mateo') || voiceStr.includes('masculina') || voiceStr.includes('face330a542b4357aa0611779c64c12f');
                
                const voices = window.speechSynthesis.getVoices();
                const esVoices = voices.filter(v => (v.lang && (v.lang.startsWith('es') || v.lang.startsWith('ES'))));
                
                let chosenVoice = null;
                if (isMaleVoice) {
                  chosenVoice = esVoices.find(v => /pablo|mateo|darío|dario|julio|jorge|raúl|raul|enrique|manuel|male|hombre|david|alvaro|ávaro/i.test(v.name))
                             || esVoices.find(v => !/helena|laura|monica|mónica|lucia|lucía|carmen|sabina|female|mujer|victoria|paloma/i.test(v.name))
                             || esVoices[0];
                } else {
                  chosenVoice = esVoices.find(v => /helena|laura|monica|mónica|lucia|lucía|carmen|sabina|female|mujer|victoria|paloma/i.test(v.name))
                             || esVoices[0];
                }

                if (chosenVoice) utter.voice = chosenVoice;

                photoImg.style.transform = 'scale(1.06)';
                photoImg.style.filter = 'brightness(1.15)';
                if (playBtn) playBtn.style.transform = 'translate(-50%,-50%) scale(1.1)';

                utter.onend = () => {
                  photoImg.style.transform = 'scale(1)';
                  photoImg.style.filter = 'none';
                  if (playBtn) playBtn.style.transform = 'translate(-50%,-50%) scale(1)';
                };
                utter.onerror = () => {
                  photoImg.style.transform = 'scale(1)';
                  photoImg.style.filter = 'none';
                  if (playBtn) playBtn.style.transform = 'translate(-50%,-50%) scale(1)';
                };

                window.speechSynthesis.speak(utter);
                toast({ message: `🗣️ Reproduciendo voz del avatar (${chosenVoice ? chosenVoice.name : 'Voz Masculina'})`, type: 'info' });
              }
            };
            card.addEventListener('click', speakPhotoSpeech);
          }
        });
      };

      // Invocar enlace de eventos para ambos contenedores de creaciones
      bindCardEvents(container, avatarItems.slice(0, 6));
      bindCardEvents(allCreacionesContainer, avatarItems);

      // Evento eliminar en la lista de creaciones de avatar
      qsa('.btn-del-avatar-gen').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (!id) return;

          const confirmed = await showConfirmModal({
            title: '¿Eliminar avatar?',
            message: 'Esta creación de avatar se eliminará permanentemente.',
            confirmText: 'Sí, eliminar',
            cancelText: 'Cancelar'
          });

          if (confirmed) {
            try {
              const { deleteGeneration } = await import('./api.js');
              await deleteGeneration(id);
              btn.closest('.av-gen-card')?.remove();
              toast({ message: 'Avatar eliminado correctamente', type: 'success' });
            } catch (err) {
              console.error('Error al eliminar avatar:', err);
              toast({ message: 'Error al eliminar el avatar', type: 'error' });
            }
          }
        });
      });

      // Polling activo seguro para elementos pendientes (evita bucles infinitos)
      const pendingItems = avatarItems.filter(item => (item.status === 'queued' || item.status === 'processing') && item.jobId);
      if (pendingItems.length > 0) {
        const { getJobStatus } = await import('./api.js');
        pendingItems.forEach(item => {
          if (activeAvatarPollMap.has(item.jobId)) return;

          let attempts = 0;
          const pollId = setInterval(async () => {
            attempts++;
            try {
              const statusRes = await getJobStatus(item.jobId);
              if (statusRes.status === 'completed' || statusRes.status === 'failed') {
                clearInterval(pollId);
                activeAvatarPollMap.delete(item.jobId);
                if (statusRes.status === 'completed') {
                  toast({ message: '✅ Vídeo de avatar generado y listo para reproducir.', type: 'success' });
                }
                await loadRecentAvatarGenerations();
              } else if (attempts >= 100) {
                // Dejar de sondear tras ~6.5 minutos
                clearInterval(pollId);
                activeAvatarPollMap.delete(item.jobId);
              }
            } catch (err) {
              if (attempts >= 100) {
                clearInterval(pollId);
                activeAvatarPollMap.delete(item.jobId);
              }
            }
          }, 4000);
          activeAvatarPollMap.set(item.jobId, pollId);
        });
      }

    } catch (err) {
      console.error('[AVATAR] Error al cargar generaciones recientes:', err);
    } finally {
      isFetchingRecentGenerations = false;
    }
  }

  // Manejador de selección de tarjetas de avatares en la rejilla (#avFotoThumbGrid)
  on(document, 'click', '.av-thumb-card', (e, card) => {
    if (card.classList.contains('av-thumb-upload-btn') || e.target.closest('.av-thumb-del-btn')) return;
    qsa('.av-thumb-card').forEach(c => c.classList.remove('is-selected'));
    card.classList.add('is-selected');
    const avatarSlug = (card.dataset.avatar || '').toLowerCase().trim();
    const avatarName = card.querySelector('.av-thumb-name')?.textContent || 'Avatar';

    // Auto-asignación inteligente de voz basada en el género del avatar (si no eligió voz manual)
    if (!userHasManuallyChosenVoice) {
      const isFemale = FEMALE_AVATARS.includes(avatarSlug) || avatarSlug === 'annie';
      selectedVoiceId = isFemale ? VOICE_FEMALE_ID : VOICE_MALE_ID;
      selectedVoiceName = isFemale ? VOICE_FEMALE_NAME : VOICE_MALE_NAME;
      updateVoicePill(selectedVoiceName);
      toast({ message: `🎯 Avatar: ${avatarName} · Voz: ${selectedVoiceName}`, type: 'info' });
    } else {
      toast({ message: `🎯 Avatar seleccionado: ${avatarName}`, type: 'info' });
    }
  });



  // Carga inicial de generaciones recientes
  loadRecentAvatarGenerations();

  const btnRefresh = qs('#btnRefreshCreaciones');
  if (btnRefresh) {
    btnRefresh.addEventListener('click', () => {
      loadRecentAvatarGenerations();
      toast({ message: '🔄 Creaciones actualizadas', type: 'info' });
    });
  }

  // ── Generar Vídeo de Avatar (Único Listener Limpio con Validaciones) ─────────
  on(document, 'click', '[data-action="generate-avatar"]', async (e, btn) => {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const scriptInput = qs('#avatarScript');
    let text = scriptInput ? scriptInput.value.trim() : '';

    // 1. Validación de guion / audio
    if (!text && !activeAudioTrack) {
      toast({ message: '⚠️ Por favor, escribe un guion o graba/sube una pista de audio para el avatar.', type: 'warning' });
      if (scriptInput) {
        scriptInput.focus();
        scriptInput.style.borderColor = '#ec4899';
        scriptInput.style.boxShadow = '0 0 12px rgba(236, 72, 153, 0.4)';
        setTimeout(() => {
          scriptInput.style.borderColor = '';
          scriptInput.style.boxShadow = '';
        }, 2000);
      }
      return;
    }

    if (text.length > 840) {
      toast({ message: '⚠️ El guion supera el límite máximo de 840 caracteres.', type: 'warning' });
      scriptInput?.focus();
      return;
    }

    if (!text && activeAudioTrack) {
      text = activeAudioTrack.name || 'Audio de voz sincronizado';
    }

    // 2. Selección de avatar (funciona idéntico en guion y foto)
    let avatarId = 'annie';
    let avatarImg = '/uploads/avatar_annie.png';
    let attachments = [];

    const selectedCard = qs('.av-thumb-card.is-selected');
    const uploadsUrl = getUploadsUrl();
    if (selectedCard) {
      const imgEl = selectedCard.querySelector('img, video');
      const imgSrc = imgEl ? (imgEl.getAttribute('src') || imgEl.src) : null;
      let rawAvatar = selectedCard.dataset.avatar || 'annie';

      avatarId = rawAvatar;
      avatarImg = imgSrc || `${uploadsUrl}/avatar_${rawAvatar}.png`;
    } else {
      avatarId = 'annie';
      avatarImg = `${uploadsUrl}/avatar_annie.png`;
    }

    const knownCatalogAvatars = ['annie', 'shawn', 'sofia', 'david', 'clara', 'marcos', 'nadia', 'denise', 'leo'];
    if (knownCatalogAvatars.includes((avatarId || '').toLowerCase().trim())) {
      attachments = [];
    } else if (avatarId && (avatarId.startsWith('http') || avatarId.includes('/uploads/'))) {
      attachments = [avatarId];
    }

    // 3. Garantía y fallback de voz
    if (!selectedVoiceId) {
      const isFemale = FEMALE_AVATARS.includes((avatarId || '').toLowerCase());
      selectedVoiceId = isFemale ? VOICE_FEMALE_ID : VOICE_MALE_ID;
      selectedVoiceName = isFemale ? VOICE_FEMALE_NAME : VOICE_MALE_NAME;
      updateVoicePill(selectedVoiceName);
    }

    // 4. Bloqueo anti-doble clic y estado visual de carga
    const originalBtnHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" style="width:1rem; height:1rem; border-width:2px; margin-right:6px;"></span> Iniciando…`;

    try {
      // 1. Enviar la petición al backend con audioUrl si existe
      toast({ message: '⏳ Enviando solicitud a la IA...', type: 'info' });
      const response = await apiRequest('/generate/avatar', {
        method: 'POST',
        body: JSON.stringify({
          text,
          avatarId,
          attachments,
          audioUrl: activeAudioTrack ? activeAudioTrack.url : null,
          voiceId: selectedVoiceId || 'face330a542b4357aa0611779c64c12f',
          voiceName: selectedVoiceName || 'Darío (Masculina, ES)',
        }),
      });

      toast({ message: '🚀 ¡Creación iniciada! Tu avatar se está procesando.', type: 'success' });

      // 2. Cambiar a la pestaña "Mis Creaciones"
      const topTabCreaciones = qs('[data-action="av-tab"][data-tab="mis-creaciones"]');
      if (topTabCreaciones) {
        topTabCreaciones.click();
      }

      // 3. Recargar la lista de creaciones para mostrar la tarjeta con el spinner
      await loadRecentAvatarGenerations();

      const recentList = qs('#avAllCreacionesList') || qs('#avRecentGenerationsList');
      if (recentList && !recentList.querySelector(`[data-job-id="${response?.jobId}"]`)) {
        const loadingCard = document.createElement('div');
        loadingCard.id = 'tempLoadingCard';
        loadingCard.className = 'av-gen-card';
        loadingCard.style.cssText = 'background: var(--color-bg-surface-2, #1c1c1e); border-radius:12px; padding:12px; position:relative; border: 1px solid rgba(168,85,247,0.3);';
        loadingCard.innerHTML = `
          <div style="position:relative; margin-bottom:8px;">
            <img src="${avatarImg}" style="width:100%; height:140px; object-fit:cover; border-radius:10px; filter: brightness(0.75);" />
            <div style="position:absolute; top:8px; left:8px; background:rgba(99,102,241,0.9); color:white; padding:4px 10px; border-radius:12px; font-size:0.75rem; display:flex; align-items:center; gap:6px;">
              <span class="spinner-border spinner-border-sm" role="status"></span> Generando vídeo IA...
            </div>
          </div>
          <div>
            <h4 style="font-size:0.9rem; font-weight:600; margin:0 0 4px 0; color:white;">Avatar parlante en proceso</h4>
            <p style="font-size:0.78rem; color: #a1a1aa; margin:0;">"${escapeHtml(text.substring(0, 70))}..."</p>
          </div>
        `;
        recentList.prepend(loadingCard);
      }

      if (response && response.jobId) {
        toast({ message: '⏳ Generando vídeo de avatar parlante en la IA...', type: 'info' });
        
        let attempts = 0;
        const maxAttempts = 150; // 7.5 minutos máximo (cubre colas de HeyGen)
        const pollInterval = setInterval(async () => {
          attempts++;
          try {
            const jobData = await apiRequest(`/jobs/${response.jobId}`);
            if (jobData && (jobData.status === 'completed' || jobData.status === 'failed')) {
              clearInterval(pollInterval);
              qs('#tempLoadingCard')?.remove();
              if (jobData.status === 'completed') {
                toast({ message: '✅ ¡Vídeo de avatar MP4 generado con éxito!', type: 'success' });
              } else {
                toast({ message: `⚠️ La generación de vídeo finalizó: ${jobData.error || 'Generado'}`, type: 'info' });
              }
              await loadRecentAvatarGenerations();
            }
          } catch (e) {
            console.warn('[AVATAR] Error sondeando trabajo:', e);
          }
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            qs('#tempLoadingCard')?.remove();
            await loadRecentAvatarGenerations();
          }
        }, 3000);
      } else {
        toast({ message: '✅ Generación iniciada con éxito.', type: 'success' });
        qs('#tempLoadingCard')?.remove();
        await loadRecentAvatarGenerations();
      }

    } catch (err) {
      console.error('[AVATAR] Error al generar:', err);
      const errStr = err.message || '';
      if (errStr.includes('ALERTA DE') || errStr.includes('CRÉDITOS') || errStr.includes('CUOTA') || errStr.includes('Modo Admin') || errStr.includes('402') || errStr.includes('limit') || errStr.includes('exceeded')) {
        showAdminCreditModal({
          title: '⚠️ ALERTA DE CRÉDITOS / CUOTA DE PROVEEDOR IA',
          message: errStr.replace(/^Error [^:]+:\s*/i, ''),
          provider: 'HeyGen / Fal.ai',
        });
      }
      toast({ message: `Error al generar avatar: ${errStr}`, type: 'error' });
      qs('#tempLoadingCard')?.remove();
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalBtnHtml;
    }
  });
} // fin de initAvatarPage
